/**
 * RWBR — THE SEASON, PLAYED ONE RACE AT A TIME.
 *
 * Thirty-six Race Weeks and thirty-six races, strung together so that what
 * happened in March is still true in November. D-004 fixes the length; D-012
 * fixes the shape (26 regular-season races, then a ten-race playoff that cuts
 * 16 to 12 to 8 to 4); D-023 fixes the scoring. Everything else in this file is
 * CONTINUITY — the part that turns a stack of independent races into a world.
 *
 * ===========================================================================
 * WHY THIS FILE EXISTS: THE LOOP WAS A PRISON
 * ===========================================================================
 *
 * `runSeason` used to BE the season — one `for` loop over thirty-six races, in
 * `season.ts`. Everything that carried between races (the feud list, the fume
 * list, the TV-list hold-overs, suspensions, injuries, transfers in progress,
 * the interview effect owed to next Sunday and the chips that come with it, the
 * ledger, the standings fold, the playoff bracket, the living roster) lived in a
 * LOCAL VARIABLE inside that loop. Local variables cannot be saved, shown or
 * resumed, and that one fact is what kept three features at "exhibition only":
 *
 *   • MANUAL DICE (D-018). You could hand-roll a one-off race but not a season
 *     race, because there was no way to get a season to the green flag of race
 *     14 and stop there.
 *   • RACE-BY-RACE PLAY. The race-week screen could start a season and play
 *     race 1 and no further, because race 2 needs race 1's carry and the carry
 *     was not a thing you could hold. (It said so on screen at length, behind a
 *     constant called `SEASON_HANDOFF_LIMIT`. Both are gone: the screen now
 *     plays all 36 one at a time off a `SeasonState` the shell owns.)
 *   • WATCHING A SEASON UNFOLD AT ALL.
 *
 * So the carry is an OBJECT now — `SeasonState`, serialisable, inspectable,
 * saveable between races — and the loop body is a function of it,
 * `runSeasonRace`.
 *
 * ===========================================================================
 * THE PROPERTY THAT MATTERS MORE THAN EVERYTHING ELSE HERE
 * ===========================================================================
 *
 * `runSeason` is now `beginSeason` + `runSeasonRace` × 36 + `finishSeason`. It
 * is not a second implementation that happens to agree with this one; it is
 * this one, called in a loop. There is exactly ONE season procedure in the
 * project and the fast path cannot drift from the slow one because there is no
 * separate fast path.
 *
 * `npm run seasonplay` proves it by COUNTING rather than by asserting: several
 * seeds, each played three ways — all at once, race by race, and race by race
 * with the state pushed through `JSON.parse(JSON.stringify(...))` between every
 * pair of races — with the champion, every driver's points, every ledger event,
 * every Megabuck and every money row compared and printed.
 *
 * WHAT CARRIES BETWEEN RACES, AND WHAT DOES NOT
 *
 *   FEUDS carry until they burn out. The rulebook left this to taste — p.xvi,
 *   "you can say that when a driver 'spins out' after losing a DUEL, he goes on
 *   the FUME list. Or, in circumstances where it makes sense, you might decide
 *   that a FEUD has just been started." EZRA S2 replaces the discretion with a
 *   table and cites the page: "Feud Losers (RB/xvi): Roll d6: 1 None 2-3 Feud;
 *   4-5 Fume; 6 Both." That d6 is rolled here twice — once for every driver who
 *   lost a duel during the race, and once for every feud already on the list,
 *   which is what makes a grudge something that ends rather than something that
 *   accumulates forever.
 *
 *   SUSPENSIONS count down in RACES. RWEG's Z chart suspends a driver for
 *   `[dice]` races, ruled 1d6 by D-028 §1, and the week it was issued is the
 *   first of them (`Suspension.includesThisRace`).
 *
 *   INJURIES count down the same way; a season-ending injury ends the season.
 *
 *   DEATH is permanent. TV EVENT Z 13 is implemented as printed (D-028 §2) and
 *   the driver leaves the licensed roster for good. A chartered team signs a
 *   rookie off `driverGen.ts`; an independent simply stops turning up.
 *
 *   TEAM TRANSFERS land after their delay. Z 16 has a driver quit and sign
 *   elsewhere `[dice]` weeks later; he does not race in between, because he has
 *   no car (D-028 §4, and raceWeek's own D-RW-9).
 *
 *   THE TV LIST carries. RWEG/p.3 — "certain drivers from the previous week's
 *   race may make the next week's TV LIST, without rolling on the TV LIST
 *   charts." The previous race's winner, the drivers the cameras went to
 *   afterwards, and anyone whose feud is still live.
 *
 *   CHIPS DO NOT CARRY. p.xv keeps them out of the BOTTOM group even inside one
 *   race; nothing in any source carries one across a checkered flag.
 *
 * PURITY. `runSeason` is a pure function of its input. It builds its own dice
 * off the seed, reads nothing from disk beyond the imported track and chart
 * JSON, mutates none of its arguments, and returns every event it produced.
 */

import {
  Dice,
  DiceNeeded,
  type DiceFeed,
  type DiceRequest,
  type DieDraw,
} from './dice.ts';
import { describeRequest } from './manualRace.ts';
import type { DriverCard, FeudEntry, RaceGrants } from './types.ts';
import {
  rollPostRaceInterview,
  type InterviewEligibility,
} from './postRaceInterview.ts';
import {
  applyInterviewAction,
  grantsAreEmpty,
  INTERVIEW_DURATION_CITE,
  type InterviewEffect,
} from './interviewEffects.ts';
import type { RuleEvent } from './ruleEvent.ts';
import {
  runRaceWeek,
  toRaceSetup,
  MAX_STARTING_FIELD,
  type RaceWeekOptions,
  type RaceWeekResult,
} from './raceWeek.ts';
import { runRace, type RaceOptions, type RaceResult } from './race.ts';
import { trackCard, type TurnResult } from './turn.ts';
import { applyAlternateCards } from './altCards.ts';
import {
  buildSchedule,
  isPlayoffRace,
  PLAYOFF_ROUNDS,
  REGULAR_SEASON_RACES,
  SEASON_RACES,
  type PlayoffRound,
  type ScheduledRace,
  type SeasonSchedule,
} from './schedule.ts';
import {
  absenceEvent,
  championshipOrder,
  compareDrivers,
  entryEvent,
  stageEvent,
  foldSeason,
  resultEvent,
  SEASON_EVENT_TYPE,
  type AbsenceReason,
  type DriverSeasonStats,
  type SeasonEvent,
  type SeasonPhase,
  type SeasonStandings,
} from './standings.ts';
import { createDriver } from './driverGen.ts';
import { describeConsequence } from './careerConsequences.ts';
import { rookieAge } from './aging.ts';
import { CAR_NUMBERS, FIRST_NAMES, SURNAMES, TEAM_SIZE } from './field.ts';
// The books. Only the per-race step is imported, and only so that a season
// being played one race at a time can say what the weekend paid — see
// `SeasonFinanceTrackState` for what is deliberately NOT settled here.
import {
  runSeasonFinanceRace,
  teamMap,
  type FinanceCarry,
  type SeasonFinanceOptions,
} from './finance/seasonFinance.ts';
import {
  openingFinanceState,
  profileFromCard,
  lastTurnWinMethod,
  raceFactsForNextWeek,
  type FinanceDriverProfile,
  type FinanceState,
  type PreviousRaceFacts,
  type RaceFinanceResult,
  type SponsorChange,
} from './finance/raceFinance.ts';
import type { MoneyEvent } from './finance/money.ts';
import type { CareerCarry } from './careerCarry.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_SEASON = 'D-004 + D-012 — 36 races: 26 regular season, then a 10-race playoff';
export const CITE_FEUD_LOSERS =
  'RB/xvi via EZRA S2 — "Feud Losers (RB/xvi): Roll d6: 1 None 2-3 Feud; 4-5 Fume; 6 Both"';
export const CITE_CARRYOVER_TV =
  'RWEG/p.3 — "certain drivers from the previous week\'s race may make the next week\'s TV LIST, without rolling on the TV LIST charts"';
export const CITE_SUSPENSION = 'RWEG/p.10 TV EVENT Z + D-028 §1 — suspension of [dice] races, ruled 1d6';
export const CITE_DEATH = 'RWEG/p.10 TV EVENT Z 13 + D-028 §2 — implemented as printed, default on';
export const CITE_TRANSFER = 'RWEG/p.10 TV EVENT Z 16 + D-028 §4 — driver quits and signs elsewhere after [dice] weeks';
export const CITE_NO_CHIP_CARRY =
  'RB/xv — performance chips are a within-race currency; nothing in any source carries one past a checkered flag';
export const CITE_PLAYOFF_QUALIFY =
  'D-012 — "every race winner qualifies first; remaining berths go to points; ties broken on average points per start"';
export const CITE_ROUND_WIN = 'D-012 / EZRA Chase — a round win advances a driver automatically';

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/** The garage, as far as the season loop cares about it. */
export interface SeasonTeam {
  teamId: string;
  name: string;
  driverIds: readonly string[];
}

export interface SeasonOptions {
  /** The starting field each week. Default 43 (RWEG/p.4 STEP SIX). */
  fieldSize?: number;
  /** TV EVENT Z 13. Default true — D-028 §2. */
  allowDriverDeath?: boolean;
  /** A chartered team replaces a dead driver with a rookie. Default true. */
  signRookies?: boolean;
  /** Passed through to every race. */
  race?: RaceOptions;
  /** Passed through to every Race Week. */
  raceWeek?: Pick<RaceWeekOptions, 'diceRaces' | 'tvQualityFor'>;
  /**
   * How many drivers may join a TV list without rolling (RWEG/p.3). Default 6.
   * AMBIGUITY: the booklet names the categories and prints no cap; without one,
   * a busy week can carry a dozen names into the next TV list and swamp the 2d6
   * draw the list is supposed to be.
   */
  maxCarryOverTv?: number;
  /**
   * Safety rail on the feud list. Default 12. The RB/xvi roll retires half the
   * list every week on average, so this should never bind; it exists so a rules
   * change cannot quietly turn the season into a demolition derby.
   */
  maxCarriedFeuds?: number;
  /**
   * The POST RACE INTERVIEW chart (`RWBR_postraceinterview.pdf`). Default ON.
   *
   * Pass `false` for the base game — the chart is an add-on sheet, not part of
   * the boxed rulebook, and 15 of its 36 rows carry a reconstructed within-band
   * placement (D-EZ-11). Anyone who would rather not roll on those can turn the
   * whole thing off in one flag.
   */
  postRaceInterview?: false;
  /**
   * D-EZ-98 — optional switches off Howard's own sheet, `Season Points 3.xlsx`.
   * Both are HOUSE RULES and both are off by default; the base game (D-023)
   * scores a DNF on its classified position and has no stages.
   *
   *   dnfScoresZero  a car not running at the flag scores no finishing points.
   *   stagePoints    'tracked' — the race is cut into thirds of its turns and
   *                  the top ten running at the end of stages one and two are
   *                  credited 10 down to 1, shown separately and NOT added;
   *                  'counted' — the same, added to the championship.
   */
  houseRules?: SeasonHouseRules;
}

export interface SeasonHouseRules {
  dnfScoresZero?: boolean;
  stagePoints?: 'off' | 'tracked' | 'counted';
}

/**
 * D-EZ-94 — WHAT A SEASON HANDS THE NEXT ONE ACROSS THE WINTER.
 *
 * Injuries and suspensions with races still to serve carry into the new year
 * (a driver hurt in the finale misses the opener). Feuds carry too, already
 * thinned by the winter's own cooling-off roll (see `offseason.ts`), and keep
 * cooling through the weekly RB/xvi roll from race one unless they re-ignite.
 *
 * Out-for-the-season injuries heal over the winter and are not here.
 */
export interface SeasonCarryIn {
  feuds: FeudEntry[];
  /** [driverId, races still to sit out]. */
  suspended: [string, number][];
  /** [driverId, races still to sit out]. */
  injured: [string, number][];
}

/** The same facts, as they stood at the last checkered flag. */
export interface SeasonCarryOut {
  /** Every feud still live after the finale's RB/xvi roll. */
  feuds: FeudEntry[];
  suspended: [string, number][];
  /** Numeric injuries only; 'season' injuries heal over the winter. */
  injured: [string, number][];
}

export interface SeasonInput {
  seed: number;
  /** The LICENSED ROSTER — ~53 cards (D-024, N-011), not a 43-car field. */
  roster: readonly DriverCard[];
  /** The charter. Optional; without it every driver is treated as independent. */
  teams?: readonly SeasonTeam[];
  /** Defaults to `buildSchedule(seed)`. */
  schedule?: SeasonSchedule;
  seasonId?: string;
  options?: SeasonOptions;
  /** D-EZ-94 — feuds, injuries and suspensions carried out of last winter. */
  carryIn?: SeasonCarryIn;
  /**
   * EVERYTHING last winter handed on (`OffseasonResult.carry`). Held on the
   * state and handed back on the report, so whoever settles the year's books
   * reads the same contracts and banks the season opened with, however many
   * saves and loads lie between. Supplies `carryIn` when that is not given.
   */
  inherited?: CareerCarry;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

export interface SeasonRaceResult {
  raceNumber: number;
  scheduled: ScheduledRace;
  week: RaceWeekResult;
  race: RaceResult;
  /** 1-based grid slot, by driver id. */
  startPositions: Record<string, number>;
  /** Everyone who tied for the most turns led. Empty if nobody led a turn. */
  mostTurnsLedIds: string[];
  /** TV EVENT X 13 — forfeited this race's championship points. */
  pointsForfeitedIds: string[];
  /** Who could not turn up, and why. */
  absences: { driverId: string; reason: AbsenceReason; note: string }[];
  /** Running standings AFTER this race. */
  standings: SeasonStandings;
  /** This race's slice of the season ledger, in order. */
  events: SeasonEvent[];
  weekSeed: number;
  raceSeed: number;
  /**
   * D-EZ-108 — the rivalry engine's digest of this race's tape (who wrecked
   * whom, duels, feud flare-ups, battles). NEVER set by the engine: the archive
   * writes it just before it strips the tape (`lib/seasonArchive.ts`), so a
   * settled season remembers its incidents. Narrative only; nothing reads it
   * back into a race.
   */
  memory?: import('./people/memory.ts').RaceMemory;
}

export interface PlayoffSeed {
  driverId: string;
  name: string;
  /** 1-16. */
  seed: number;
  /** True when he is in on the strength of a win, not on points. */
  viaWin: boolean;
  wins: number;
  points: number;
  avgPointsPerStart: number;
}

export interface PlayoffRoundResult {
  round: PlayoffRound;
  label: string;
  races: number[];
  /** Everyone who entered the round, in seed order. */
  contenders: string[];
  /** Points scored INSIDE this round, by contender. */
  roundPoints: Record<string, number>;
  /** Contenders who won a race in the round and advanced on the spot. */
  autoAdvanced: string[];
  advanced: string[];
  eliminated: string[];
}

export interface PlayoffBracket {
  /** The Round of 16 field, seeded after race 26. */
  seeds: PlayoffSeed[];
  rounds: PlayoffRoundResult[];
  finalFour: string[];
  championId: string | null;
  /** The champion's points in the championship race. */
  championRacePoints: number | null;
}

export interface RookieSigning {
  driverId: string;
  name: string;
  teamId: string;
  /** The driver he replaces. */
  replacing: string;
  /** Signed after this race. */
  raceNumber: number;
}

export interface SeasonCasualty {
  driverId: string;
  name: string;
  raceNumber: number;
  text: string;
}

export interface FeudThread {
  a: string;
  b: string;
  /** Race numbers this feud was live for, ascending. */
  races: number[];
  startedRace: number;
  endedRace: number | null;
  /** How the RB/xvi roll finally closed it. */
  endedBy: 'cooled-off' | 'became-fume' | 'season-ended' | null;
}

export interface SeasonResult {
  seasonId: string;
  seed: number;
  schedule: SeasonSchedule;
  races: SeasonRaceResult[];
  /** Index i holds the standings after race i+1. */
  standingsAfterRace: SeasonStandings[];
  finalStandings: SeasonStandings;
  playoff: PlayoffBracket;
  championId: string | null;
  /** The whole season, one append-only stream. */
  events: SeasonEvent[];
  /** The licensed roster as it stood at the last checkered flag. */
  finalRoster: DriverCard[];
  rookies: RookieSigning[];
  fatalities: SeasonCasualty[];
  /** Every feud the season ran, with the races it was live for. */
  feudThreads: FeudThread[];
  /**
   * The points-order table as it stood after the last race, before the
   * playoff reordered it (D-EZ-90). `finalStandings` is the championship.
   */
  pointsStandings: SeasonStandings;
  /** D-EZ-94 — what is still owed when the season ends. */
  carryOut: SeasonCarryOut;
  /** What the season was begun with (`SeasonInput.inherited`), or null. */
  inherited: CareerCarry | null;
}

// ---------------------------------------------------------------------------
// RB/xvi — what a duel loser, or a live feud, does next
// ---------------------------------------------------------------------------

export type FeudLoserOutcome = 'none' | 'feud' | 'fume' | 'both';

/**
 * EZRA S2's closure of RB/xvi, exactly as printed:
 *   1 None · 2-3 Feud · 4-5 Fume · 6 Both
 */
export function feudLoserOutcome(d6: number): FeudLoserOutcome {
  if (!Number.isInteger(d6) || d6 < 1 || d6 > 6) {
    throw new RangeError(`RB/xvi is a d6; got ${d6}`);
  }
  if (d6 === 1) return 'none';
  if (d6 <= 3) return 'feud';
  if (d6 <= 5) return 'fume';
  return 'both';
}

function sameFeud(x: FeudEntry, y: FeudEntry): boolean {
  return (x.a === y.a && x.b === y.b) || (x.a === y.b && x.b === y.a);
}

function feudKey(f: FeudEntry): string {
  return f.a < f.b ? `${f.a}|${f.b}` : `${f.b}|${f.a}`;
}

// ---------------------------------------------------------------------------
// Seeds
// ---------------------------------------------------------------------------

/**
 * One stored seed per race (D-005), derived from the season seed so a whole
 * season replays from one number. `salt` keeps the race week, the race and the
 * between-races rolls off each other's stream, so adding a roll in one cannot
 * shift the dice in another.
 */
export function raceSeedFor(seasonSeed: number, raceNumber: number, salt: number): number {
  let h = (seasonSeed ^ Math.imul(raceNumber, 0x9e3779b1) ^ Math.imul(salt + 1, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

const SALT_WEEK = 0;
const SALT_RACE = 1;
const SALT_BETWEEN = 2;
const SALT_ROOKIE = 3;
/**
 * A FIFTH random stream, for the same reason traits were dealt on a fourth:
 * the post-race interview must not repaint a single die anywhere else.
 *
 * Rolling it off `betweenDice` would have worked and would have been wrong —
 * every roll `settleTransfers` makes after it would shift, so turning the chart
 * on would silently change who signs where. `SALT_ROOKIE` is consumed as
 * `SALT_ROOKIE + index`, so this sits well clear of it.
 */
const SALT_INTERVIEW = 1000;

// ---------------------------------------------------------------------------
// Carried state
// ---------------------------------------------------------------------------

interface Carry {
  feuds: FeudEntry[];
  /** Fuming into the NEXT race only; race week generates its own each week. */
  fuming: string[];
  /** Races still to sit out. */
  suspended: Map<string, number>;
  /** Races still to sit out, or 'season'. */
  injured: Map<string, number | 'season'>;
  dead: Set<string>;
  /** Quit his team; races until he signs with the next one. */
  inTransfer: Map<string, { racesLeft: number; fromTeamId: string | null }>;
  carryOverTvIds: string[];
  /**
   * POST RACE INTERVIEW effects owed to the NEXT race and no further (D-EZ-15).
   *
   * Cleared the moment they are applied, which is what makes "for next race"
   * true by construction rather than by anyone remembering to undo it.
   */
  interview: Map<string, InterviewEffect>;
  /** Drivers the interview put on the TV list; folded into `carryOverTvIds`. */
  interviewTv: string[];
  /**
   * Drivers whose FIRST duel next race rolls straight onto the Feud/Fume chart
   * ("FIRST DUEL- ROLL ON FEUD/FUME CHART"). Recorded and surfaced; the duel
   * module has no seam for it yet, so it is reported, not silently dropped.
   */
  interviewFirstDuel: string[];
}

/**
 * Race week's own performance chips, plus anything an interview handed over.
 * Added rather than overwritten — a driver can earn one in qualifying AND be
 * owed one by the chart, and neither is a reason to lose the other.
 */
function mergeChips(
  ...sources: readonly Record<string, number>[]
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const source of sources) {
    for (const [id, n] of Object.entries(source)) {
      if (n > 0) out[id] = (out[id] ?? 0) + n;
    }
  }
  return out;
}

/**
 * A fresh `Carry` is now built by `beginSeason` in its SERIALISED form and
 * hydrated per race, so there is no `newCarry()` here any more. `Carry` above is
 * the working form — the Maps and Sets the rule code below has always read —
 * and `CarryState` is the same facts as data. See `hydrate`.
 */

// ---------------------------------------------------------------------------
// The post-race interview
// ---------------------------------------------------------------------------

/**
 * Who is standing in front of a microphone, by the sheet's own six categories.
 *
 * The chart module deliberately does not compute this — only the season layer
 * knows the FUME list and the FEUD list. Each band's definition is quoted where
 * it is built so the reading can be checked against the sheet without leaving
 * the file.
 */
function interviewEligibility(
  race: RaceResult,
  carry: Carry,
  roster: ReadonlyMap<string, DriverCard>,
): InterviewEligibility {
  const live = (id: string): boolean => roster.has(id) && !carry.dead.has(id);

  // "Refers to racers that were placed on list THIS race." `carryFeuds` has
  // already run, so `carry.fuming` is exactly that list.
  const fume = carry.fuming.filter(live);

  // "Randomly choose ONE racer." Read as the drivers who FINISHED in the TOP
  // (2nd-7th) or MIDDLE (8th-19th) group — the board's own geometry, and the
  // only reading under which the category is ever empty in a 43-car field.
  const topOrMiddle = race.finishingOrder
    .filter((f) => f.status === 'running' && f.place >= 2 && f.place <= 19)
    .map((f) => f.driverId)
    .filter(live);

  // "A racer who wrecked out of race (Trouble or Problem)." `repair-failed` is
  // how a collected car actually leaves, and `injury` is EZRA's new way out of
  // the same incident — both are wrecking out, whatever the field is called.
  const WRECKED_OUT = new Set(['trouble', 'problem', 'repair-failed', 'injury']);
  const wrecked = race.finishingOrder
    .filter((f) => f.status === 'out' && f.outReason && WRECKED_OUT.has(f.outReason))
    .map((f) => f.driverId)
    .filter(live);

  // "Refers to Non-Wrecked racers only" — out of the race, but not in a wreck.
  const dnf = race.finishingOrder
    .filter((f) => f.status === 'out' && (!f.outReason || !WRECKED_OUT.has(f.outReason)))
    .map((f) => f.driverId)
    .filter(live);

  // "Winner of race."
  const winner = race.winnerId && live(race.winnerId) ? [race.winnerId] : [];

  // "One random racer that will be placed on list for NEXT race." Anyone who
  // ran and is not already on the feud list — putting a man on a list he is
  // already on is not a result.
  const feuding = new Set(carry.feuds.flatMap((f) => [f.a, f.b]));
  const feud = race.finishingOrder
    .map((f) => f.driverId)
    .filter((id) => live(id) && !feuding.has(id));

  return { fume, topOrMiddle, wrecked, dnf, winner, feud };
}

/**
 * Push an interview's list operations into the season's own bookkeeping.
 *
 * Every one of these lands on plumbing that already existed — the FUME list,
 * the TV list, the FEUD list — which is the whole reason this chart was worth
 * wiring rather than rebuilding around.
 */
function applyInterviewListOps(
  effect: InterviewEffect,
  carry: Carry,
  race: RaceResult,
  roster: ReadonlyMap<string, DriverCard>,
): void {
  for (const op of effect.listOps) {
    switch (op.kind) {
      case 'tv':
        if (!carry.interviewTv.includes(op.driverId)) carry.interviewTv.push(op.driverId);
        break;
      case 'fume':
        if (!carry.fuming.includes(op.driverId)) carry.fuming.push(op.driverId);
        break;
      case 'feud-ends':
        // "Feud Ends- remove racers from list." Both names, not just his.
        carry.feuds = carry.feuds.filter((f) => f.a !== op.driverId && f.b !== op.driverId);
        break;
      case 'both-to-fume': {
        // "Each of the two feuding racers are removed from the Feud List and
        // placed separately on the Fume List for the next race potentially
        // starting a new feud with other racers."
        const his = carry.feuds.filter((f) => f.a === op.driverId || f.b === op.driverId);
        for (const f of his) {
          for (const id of [f.a, f.b]) {
            if (roster.has(id) && !carry.fuming.includes(id)) carry.fuming.push(id);
          }
        }
        carry.feuds = carry.feuds.filter((f) => !his.includes(f));
        break;
      }
      case 'first-duel-feud-fume':
        if (!carry.interviewFirstDuel.includes(op.driverId)) {
          carry.interviewFirstDuel.push(op.driverId);
        }
        break;
    }
  }
  void race;
}
// ---------------------------------------------------------------------------
// THE STATE
// ---------------------------------------------------------------------------

/**
 * `Carry`, with every Map and Set flattened into an array.
 *
 * WHY ARRAYS OF PAIRS AND NOT PLAIN OBJECTS. Because ORDER IS A RESULT here.
 * `carry.suspended` and `carry.injured` are iterated to build the absence list,
 * which is emitted into the ledger in iteration order and therefore fixes the
 * `sequence` numbers of every event after it; `carry.inTransfer` is iterated by
 * `settleTransfers`, which ROLLS A DIE per settled transfer, so its order
 * decides which driver gets which face. A `Map` keeps insertion order and so
 * does an array of pairs. A plain object very nearly does — but it hoists
 * integer-like keys to the front, and "driver ids never look like integers" is
 * exactly the sort of invariant that a later id scheme breaks silently. Pairs
 * cost one `new Map(...)` per race and cannot be got wrong.
 */
export interface CarryState {
  feuds: FeudEntry[];
  fuming: string[];
  /** [driverId, races still to sit out]. */
  suspended: [string, number][];
  /** [driverId, races still to sit out, or 'season']. */
  injured: [string, number | 'season'][];
  dead: string[];
  inTransfer: [string, { racesLeft: number; fromTeamId: string | null }][];
  carryOverTvIds: string[];
  /** Owed to the NEXT race only. Emptied the moment it is applied. */
  interview: [string, InterviewEffect][];
  interviewTv: string[];
  interviewFirstDuel: string[];
}

/**
 * The books, kept race by race alongside the racing.
 *
 * OPT-IN, and off in `runSeason`. A season that is being played one race at a
 * time wants to know what the weekend paid before it runs the next one, and the
 * only honest way to do that is to settle each weekend as it happens rather
 * than to guess and reconcile later. `runSeason` leaves it off because the fold
 * it feeds — `runSeasonFinance` — prices the whole year in one call anyway, and
 * doing the same work twice would be two sets of books.
 *
 * WHAT IS HERE AND WHAT IS NOT, stated plainly because the gap is real:
 *
 *   HERE — the thirty-six race weekends. Every one of them is settled by
 *   `runSeasonFinanceRace`, which is the SAME function `runSeasonFinance`'s own
 *   loop calls, with the same arguments off the same seeds. `scripts/seasonplay.ts`
 *   measures that the events this track produces are byte-identical to the
 *   race-phase slice of the whole-season fold.
 *
 *   NOT HERE — the pre-season FAMILY WEALTH roll (SAG/25) and the winter
 *   (bonuses, royalties, renewals). Those two ends of the year share ONE `Dice`
 *   inside `runSeasonFinance`: the family-wealth 2d6s are drawn from it in
 *   March and the renewal 2d6s come off the same stream in November. Splitting
 *   that across a state machine would mean carrying a live `Dice` — a class
 *   instance wrapping a closure — in the state, which is precisely the thing
 *   that cannot be saved to a database between races. So the year-end fold
 *   stays where it is: run `runSeasonFinance` on the finished `SeasonResult`,
 *   exactly as `careerRunner` does today. This track is the LIVE VIEW of the
 *   weekends, not a second set of year-end books.
 */
export interface SeasonFinanceTrackState {
  /** driverId → the Sagliano team his money is booked under. Frozen at start. */
  teamOf: Record<string, string>;
  options: SeasonFinanceOptions;
  /** Priced off the STARTING cards, which is what every SAG table is keyed to. */
  profiles: FinanceDriverProfile[];
  /** Sponsorships, blackouts and warnings as they stand. */
  contracts: FinanceState;
  /** What last week's race did that this week's television cares about. */
  previous: PreviousRaceFacts | null;
  events: MoneyEvent[];
  races: RaceFinanceResult[];
  sponsorChanges: (SponsorChange & { raceNumber: number })[];
  /** The LAST settled race's reading — the one SAG/18 factor 6 wants. */
  gloomyEconomy: boolean;
}

/**
 * Bumped whenever the shape below changes in a way a saved season would notice.
 * A stored state carries it so a loader can refuse a state it cannot read
 * instead of half-understanding one.
 */
export const SEASON_STATE_VERSION = 2;

/**
 * EVERYTHING THE OLD `for` LOOP KEPT IN A LOCAL VARIABLE, on one object.
 *
 * The test of this type is not that it compiles: it is that
 *
 *     JSON.parse(JSON.stringify(state))
 *
 * can be handed back to `runSeasonRace` and the rest of the season comes out
 * byte-identical. `scripts/seasonplay.ts` runs a whole season that way, through
 * a round trip after every single race, and compares it to `runSeason`.
 *
 * THE ONE THING THAT DOES NOT SERIALISE is `options`, and only when a caller
 * puts a FUNCTION in it — `raceWeek.tvQualityFor`, `race.onTurn`. Those are
 * caller behaviour rather than season history; a loader that uses them has to
 * put them back after reading the state (`withSeasonOptions`). Everything else
 * on this object is numbers, strings, arrays and plain records.
 */
export interface SeasonState {
  version: number;
  seasonId: string;
  seed: number;
  schedule: SeasonSchedule;
  /** As handed to `beginSeason`. See the note above about functions. */
  options: SeasonOptions;
  /** 1-based number of the NEXT race to run. `SEASON_RACES + 1` when done. */
  nextRace: number;
  /**
   * The LICENSED ROSTER as it stands, in the order the season's own `Map` holds
   * it — which is the order it is dealt to `runRaceWeek`, and therefore the
   * order the shuffled stack is built from. Rebuilt into a `Map` for each race;
   * a rookie is appended, a dead man is spliced out, and a transfer rewrites a
   * card in place. Preserving that order is preserving the season.
   */
  roster: DriverCard[];
  teams: SeasonTeam[];
  /** Every name in use, so a rookie is never a second Denny Hamlin. */
  usedNames: string[];
  carry: CarryState;
  /** The whole season so far, one append-only stream. */
  events: SeasonEvent[];
  races: SeasonRaceResult[];
  standingsAfterRace: SeasonStandings[];
  rookies: RookieSigning[];
  fatalities: SeasonCasualty[];
  /**
   * Every feud thread the season has run. `activeFeudThreads` indexes INTO this
   * array rather than holding the objects a second time — see the note there.
   */
  feudThreads: FeudThread[];
  /**
   * [feud key, index into `feudThreads`] for every feud still live.
   *
   * THIS IS WHY IT IS AN INDEX AND NOT AN OBJECT. In the old loop the active
   * map and the thread log held the SAME object, so closing a feud through one
   * was visible through the other. `JSON.stringify` does not preserve that: a
   * round trip would hand back two separate copies, the close would land on the
   * one nobody reads, and every `endedRace` in the season report would come
   * back null — a bug that no test asserting "the season ran" would ever see.
   * An index survives a round trip; a shared reference does not.
   */
  activeFeudThreads: [string, number][];
  /** The next ledger sequence number. Monotonic across the whole season. */
  sequence: number;
  playoff: {
    seeds: PlayoffSeed[];
    rounds: PlayoffRoundResult[];
    /** Who is still alive in the bracket. Null until race 26 seeds it. */
    contenders: string[] | null;
  };
  /** The books, when the caller asked for them. Null in `runSeason`. */
  finance: SeasonFinanceTrackState | null;
  /** D-EZ-91/94 — what last winter handed on, kept for the year-end books. */
  inherited: CareerCarry | null;
}

// ---------------------------------------------------------------------------
// Starting, and knowing where you are
// ---------------------------------------------------------------------------

/** Turn the books on. Everything else about a season is in `SeasonInput`. */
export interface SeasonPlayOptions {
  finance?: {
    /**
     * driverId → finance team id. Defaults to `teamMap(roster)`, which gives
     * every driver his own single-driver Sagliano team (SAG/12 — see the note
     * on `financeTeamId`).
     */
    teamOf?: Record<string, string>;
    /** financeTeamId → shop, so SAG/25's one-fortune-per-owner rule can bite. */
    ownerOf?: Record<string, string>;
    options?: SeasonFinanceOptions;
    /**
     * D-EZ-91 — last winter's books, so the weekends are paid off the contracts
     * the cars actually carry into the year. Must be the same carry the
     * year-end `runSeasonFinance` is given, or the live view and the settled
     * books would open on different sponsors.
     */
    carry?: FinanceCarry;
  };
}

/**
 * Set a season up and run NOTHING.
 *
 * Every validation `runSeason` used to do at the top of its loop happens here,
 * so a malformed season fails before a wheel turns rather than at race 1.
 */
export function beginSeason(input: SeasonInput, play: SeasonPlayOptions = {}): SeasonState {
  const seasonId = input.seasonId ?? `season-${input.seed}`;
  const schedule = input.schedule ?? buildSchedule(input.seed);

  if (schedule.races.length !== SEASON_RACES) {
    throw new RangeError(`a season is ${SEASON_RACES} races; this schedule has ${schedule.races.length}`);
  }
  const seenIds = new Set<string>();
  for (const card of input.roster) {
    if (seenIds.has(card.driverId)) throw new Error(`duplicate driver on the roster: ${card.driverId}`);
    seenIds.add(card.driverId);
  }

  // Copies only — the caller's cards are never touched, which is what makes
  // running the same input twice give the same season twice.
  const roster = input.roster.map((c) => ({ ...c }));
  const teams = (input.teams ?? []).map((t) => ({ ...t, driverIds: [...t.driverIds] }));

  // D-EZ-94 — only what concerns a driver still licensed survives the winter.
  const licensed = new Set(roster.map((c) => c.driverId));
  const carryIn = input.carryIn ?? input.inherited?.season;
  const carriedFeuds = (carryIn?.feuds ?? [])
    .filter((f) => licensed.has(f.a) && licensed.has(f.b) && f.a !== f.b)
    .map((f) => ({ a: f.a, b: f.b }));
  const carriedSuspended = (carryIn?.suspended ?? []).filter(([id, n]) => licensed.has(id) && n > 0);
  const carriedInjured = (carryIn?.injured ?? []).filter(([id, n]) => licensed.has(id) && n > 0);

  return {
    version: SEASON_STATE_VERSION,
    seasonId,
    seed: input.seed,
    schedule,
    options: input.options ?? {},
    nextRace: 1,
    roster,
    teams,
    usedNames: [...new Set(roster.map((c) => c.name))],
    carry: {
      feuds: carriedFeuds,
      fuming: [],
      suspended: carriedSuspended.map(([id, n]) => [id, n] as [string, number]),
      injured: carriedInjured.map(([id, n]) => [id, n] as [string, number | 'season']),
      dead: [],
      inTransfer: [],
      carryOverTvIds: [],
      interview: [],
      interviewTv: [],
      interviewFirstDuel: [],
    },
    events: [],
    races: [],
    standingsAfterRace: [],
    rookies: [],
    fatalities: [],
    feudThreads: [],
    activeFeudThreads: [],
    sequence: 0,
    playoff: { seeds: [], rounds: [], contenders: null },
    finance: play.finance
      ? beginFinanceTrack(
          roster,
          { ...play.finance, carry: play.finance.carry ?? input.inherited?.finance },
          seasonId,
        )
      : null,
    inherited: input.inherited ?? null,
  };
}

function beginFinanceTrack(
  roster: readonly DriverCard[],
  input: NonNullable<SeasonPlayOptions['finance']>,
  seasonIdOf: string,
): SeasonFinanceTrackState {
  const teamOf = input.teamOf ?? teamMap(roster);
  const profiles = roster.map((c) => profileFromCard(c, teamOf[c.driverId]));
  return {
    teamOf,
    options: { ...input.options, ...(input.ownerOf ? { ownerOf: input.ownerOf } : {}) },
    profiles,
    contracts: openingFinanceState(profiles, input.carry?.contracts, seasonIdOf),
    previous: null,
    events: [],
    races: [],
    sponsorChanges: [],
    gloomyEconomy: false,
  };
}

/** True once all `SEASON_RACES` races have been run. */
export function isSeasonComplete(state: SeasonState): boolean {
  return state.nextRace > SEASON_RACES;
}

/** The 1-based number of the race that runs next, or null when there is none. */
export function nextRaceNumber(state: SeasonState): number | null {
  return isSeasonComplete(state) ? null : state.nextRace;
}

export function racesRemaining(state: SeasonState): number {
  return Math.max(0, SEASON_RACES - (state.nextRace - 1));
}

/** The calendar entry for the next race — track, distance, round, name. */
export function nextScheduledRace(state: SeasonState): ScheduledRace | null {
  return isSeasonComplete(state) ? null : state.schedule.races[state.nextRace - 1];
}

export interface SeasonProgress {
  raceNumber: number | null;
  racesRun: number;
  racesRemaining: number;
  total: number;
  complete: boolean;
  /** Where in the year this is — the calendar's own answer. */
  round: PlayoffRound | null;
  /** One line a screen can print: "Race 14 of 36 — the Southern 500, next." */
  label: string;
}

/** Where the season is, for a screen that has to say so. */
export function seasonProgress(state: SeasonState): SeasonProgress {
  const next = nextScheduledRace(state);
  return {
    raceNumber: nextRaceNumber(state),
    racesRun: state.nextRace - 1,
    racesRemaining: racesRemaining(state),
    total: SEASON_RACES,
    complete: isSeasonComplete(state),
    round: next?.round ?? null,
    label: next
      ? `Race ${next.raceNumber} of ${SEASON_RACES} — ${next.raceName} at ${next.trackName}, next.`
      : `The season is over — ${state.races.length} of ${SEASON_RACES} races run.`,
  };
}

/**
 * Put caller-supplied behaviour back on a state that came off a disk.
 *
 * `SeasonOptions` may hold functions (`raceWeek.tvQualityFor`, `race.onTurn`)
 * and functions do not survive `JSON.stringify`. This is the one-liner that
 * re-attaches them, kept here rather than left to every caller so that the
 * reason is written down next to the thing that needs it.
 */
export function withSeasonOptions(state: SeasonState, options: SeasonOptions): SeasonState {
  return { ...state, options };
}

// ---------------------------------------------------------------------------
// One race
// ---------------------------------------------------------------------------

export interface SeasonRaceOptions {
  /**
   * Merged over the season's own `options.race` for THIS RACE ONLY. Everything
   * `runRace` takes is legal here, which is what makes a season race a
   * hand-rollable race rather than a special case of one.
   */
  race?: RaceOptions;
  /**
   * D-018 — hand-rolled faces for this RACE, in the order they came off the
   * table. Shorthand for `race.dice`; giving both is refused, because two
   * sources of faces is one too many and quietly preferring either would make
   * the saved stream disagree with what the player typed.
   *
   * Feeds the race ONLY. The race week still rolls itself. For a weekend that
   * is hand-rolled end to end, see `weekend` below.
   */
  dice?: DiceFeed;
  /**
   * D-018, second half — hand-rolled faces for THE WHOLE WEEKEND, race week and
   * race, in the order they came off the table.
   *
   * Howard, 17 Aug 2026, choosing this over leaving the week to the engine: a
   * hand-rolled Sunday should be all his. Until this existed, a player rolling
   * ~900 dice for the race still had the grid, the TV list and the feuds handed
   * to him by the PRNG — the half of the weekend that decides who is even IN
   * the race.
   *
   * ⚠ ONE LIST, TWO CONSUMERS, IN ORDER. The week draws first and the race
   * takes what is left. The split is computed HERE, from what the week actually
   * consumed, and never guessed at: `runRaceWeek` may draw 30 faces or 60
   * depending on what the earlier faces said (a bad-weather read cancels
   * qualifying outright and skips three whole steps), so no caller could know
   * the boundary in advance. That is exactly why this is one option and not two
   * feeds the caller has to divide.
   *
   * Mutually exclusive with `dice` / `race.dice` — refused, same reason.
   */
  weekend?: DiceFeed;
}

export interface SeasonRaceStep {
  /** The race, exactly the object `runRace` returns. */
  race: RaceResult;
  week: RaceWeekResult;
  /** The season's record of it — grid, absences, standings, its own ledger. */
  result: SeasonRaceResult;
  /** The season, one race on. The state passed in is not touched. */
  state: SeasonState;
  /** This weekend's money, when the finance track is on. */
  finance: RaceFinanceResult | null;
}

/**
 * RUN EXACTLY ONE RACE.
 *
 * This is the body of the old `for` loop, moved verbatim, with every local
 * variable it used to carry read out of `state` at the top and written back at
 * the bottom. `runSeason` is now a loop over this function, so there is one
 * season procedure and the fast path cannot drift from the slow one.
 *
 * PURE. The state handed in is never mutated — the hydration below copies every
 * container it is going to write to — so a caller can hold on to the state
 * before a race, run the race, and still have the season as it was. That is
 * what makes "undo the last race" a matter of keeping the old object.
 *
 * THROWS `DiceNeeded` when a hand-rolled race runs out of faces. That is not a
 * failure, it is the pause: see `ManualSeasonRace` below, and `manualRace.ts`
 * for why replay is the mechanism.
 */
export function runSeasonRace(state: SeasonState, options: SeasonRaceOptions = {}): SeasonRaceStep {
  if (isSeasonComplete(state)) {
    throw new RangeError(
      `all ${SEASON_RACES} races of ${state.seasonId} have been run; call finishSeason for the report`,
    );
  }
  if (options.dice && options.race?.dice) {
    throw new Error('two dice feeds were given — pass the faces either as `dice` or as `race.dice`, not both');
  }
  if (options.weekend && (options.dice || options.race?.dice)) {
    throw new Error(
      'two dice feeds were given — `weekend` already feeds the race, so it cannot be combined with `dice` or `race.dice`',
    );
  }

  const seasonOptions = state.options;
  const fieldSize = seasonOptions.fieldSize ?? MAX_STARTING_FIELD;
  const signRookies = seasonOptions.signRookies ?? true;
  const maxCarryOverTv = seasonOptions.maxCarryOverTv ?? 6;
  const maxCarriedFeuds = seasonOptions.maxCarriedFeuds ?? 12;
  // The season's own race options, plus whatever this one race adds. A manual
  // feed arrives here and nowhere else, which is why a hand-rolled season race
  // is the same code path as an engine-rolled one.
  const raceOptions: RaceOptions = {
    ...seasonOptions.race,
    ...options.race,
    ...(options.dice ? { dice: options.dice } : {}),
  };


  const w = hydrate(state);
  const { roster, teams, usedNames, carry, events, races, standingsAfterRace, rookies, fatalities } = w;
  const seasonId = state.seasonId;
  const nameOf = (id: string): string => roster.get(id)?.name ?? id;

  const emit = (
    raceNumber: number,
    raceId: string,
    phase: SeasonPhase,
    event: RuleEvent,
    turn: number | null,
    into: SeasonEvent[],
  ): void => {
    const seasonEvent: SeasonEvent = {
      ...event,
      seasonId,
      raceNumber,
      raceId,
      phase,
      turn,
      sequence: w.sequence++,
    };
    into.push(seasonEvent);
    events.push(seasonEvent);
  };

  const scheduled = state.schedule.races[state.nextRace - 1];
  const raceNumber = scheduled.raceNumber;
  const raceId = `${seasonId}-r${String(raceNumber).padStart(2, '0')}-${scheduled.raceKey}`;
  const raceEvents: SeasonEvent[] = [];
  const weekSeed = raceSeedFor(state.seed, raceNumber, SALT_WEEK);

  /**
   * THE WEEKEND'S DICE, when the player is rolling them (`options.weekend`).
   *
   * Built HERE, rather than inside `runRaceWeek`, because the same face list
   * has to serve the week and then the race and only this function sees both.
   * The week gets a real `Dice` — that is the seam `RaceWeekInput.dice` has
   * always offered for scripted tests, used now for its first real purpose.
   */
  const weekDice = options.weekend ? new Dice(weekSeed, options.weekend) : null;
  const raceSeed = raceSeedFor(state.seed, raceNumber, SALT_RACE);
  const betweenDice = new Dice(raceSeedFor(state.seed, raceNumber, SALT_BETWEEN));
  const interviewDice = new Dice(raceSeedFor(state.seed, raceNumber, SALT_INTERVIEW));

  // ---- who is not here ------------------------------------------------
  const absences: { driverId: string; reason: AbsenceReason; note: string }[] = [];
  for (const [driverId, left] of carry.suspended) {
    if (left > 0) absences.push({ driverId, reason: 'suspended', note: `${left} race${left === 1 ? '' : 's'} of a suspension left` });
  }
  for (const [driverId, left] of carry.injured) {
    absences.push({
      driverId,
      reason: left === 'season' ? 'season-injury' : 'injured',
      note: left === 'season' ? 'out for the season' : `${left} race${left === 1 ? '' : 's'} of recovery left`,
    });
  }
  for (const [driverId, transfer] of carry.inTransfer) {
    absences.push({
      driverId,
      reason: 'quit',
      note: `between rides — ${transfer.racesLeft} week${transfer.racesLeft === 1 ? '' : 's'} until he signs`,
    });
  }
  const unavailableIds = absences.map((a) => a.driverId);
  for (const absence of absences) {
    emit(
      raceNumber,
      raceId,
      'season',
      absenceEvent(
        absence.driverId,
        nameOf(absence.driverId),
        absence.reason,
        absence.reason === 'suspended' ? CITE_SUSPENSION : CITE_SEASON,
        absence.note,
      ),
      null,
      raceEvents,
    );
  }

  // ---- RACE WEEK -------------------------------------------------------
  /*
    THE CARDS THIS FIELD RACES OFF TODAY — D-EZ-76.

    `roster` is the ledger and is never overlaid; `liveCards` is the copy the
    week is dealt from, and an alternate card lands here and nowhere else. See
    the header of `altCards.ts`: an alternate written back to the roster either
    loses the season's own edits to that card or makes the alternate permanent,
    and does it silently.

    The rule itself rides on `card.altCard` and is applied as a DIFFERENCE from
    whatever the card says today, so a driver who has been re-rated for four
    winters keeps the same road-course advantage measured from where he has got
    to. Nothing is passed in and nothing has to be plumbed — a card that has no
    alternate comes back as the identical object.
  */
  const liveCardsBase = [...roster.values()].filter((c) => !carry.dead.has(c.driverId));
  const scheduledCard = trackCard(scheduled.trackId);
  const liveCards = applyAlternateCards(liveCardsBase, {
    lengthMiles: scheduledCard.lengthMiles ?? null,
    category: scheduledCard.category,
    playoff: isPlayoffRace(raceNumber),
  });
  // A feud with a dead man is not a feud. Drop it before the week reads it.
  carry.feuds = carry.feuds.filter((f) => roster.has(f.a) && roster.has(f.b));

  const week = runRaceWeek({
    roster: liveCards,
    seed: weekSeed,
    ...(weekDice ? { dice: weekDice } : {}),
    options: {
      fieldSize,
      allowDriverDeath: seasonOptions.allowDriverDeath ?? true,
      carryOverTvIds: carry.carryOverTvIds.slice(0, maxCarryOverTv),
      carryOverFeuds: carry.feuds,
      unavailableIds,
      diceRaces: seasonOptions.raceWeek?.diceRaces,
      tvQualityFor: seasonOptions.raceWeek?.tvQualityFor,
      // EZRA RC — the only trait whose effect lands during QUALIFYING and
      // depends on where the race is, so the schedule hands it down.
      roadCourse: trackCard(scheduled.trackId).category === 'Road Course',
    },
  });
  for (const event of week.events) emit(raceNumber, raceId, 'race-week', event, null, raceEvents);

  /**
   * THE BOUNDARY BETWEEN THE WEEK'S DICE AND THE RACE'S — measured, not guessed.
   *
   * The week has now run, so how many of the player's faces it swallowed is a
   * FACT rather than an estimate, and the race takes the rest. It has to be
   * measured: `runRaceWeek` draws a different number of faces depending on what
   * the earlier faces said. A bad-weather read (TV EVENT Z 21/41/61) cancels
   * qualifying and skips three whole steps, so the same weekend can consume
   * thirty faces or sixty. No caller could divide the list in advance, which is
   * why `weekend` is one list and this is the only place it is cut.
   *
   * `entry === 'hand'` and not `stream.length`: under the `prng` exhaustion
   * policy — "that's enough, run the rest" — the week keeps drawing after the
   * player's faces run out, and those draws came off the PRNG and consumed
   * nothing. Counting them would slice the race's feed short by exactly the
   * number of dice the engine rolled, and the race would then be handed faces
   * that answer questions asked earlier in the week. Silent, and wrong.
   */
  const weekendRaceFeed: DiceFeed | null =
    options.weekend && weekDice
      ? {
          faces: options.weekend.faces.slice(
            weekDice.stream.filter((d) => d.entry === 'hand').length,
          ),
          exhausted: options.weekend.exhausted,
        }
      : null;
  if (weekendRaceFeed) raceOptions.dice = weekendRaceFeed;

  // ---- THE RACE --------------------------------------------------------
  const gridIds = new Set(week.grid.map((c) => c.driverId));
  // Fume carried out of last week's checkered flag, plus this week's own
  // PLACEMENT D fumers (D-028 §7). Only drivers who are actually racing.
  const fuming = [...new Set([...week.fuming, ...carry.fuming.filter((id) => gridIds.has(id))])];

  // ---- LAST WEEK'S INTERVIEW, CASHED IN ---------------------------------
  //
  // D-EZ-15: one race, and this is it. The card edits are swapped into the
  // grid for this race only; the grants ride on the car; the chips join race
  // week's own. `carry.interview` is emptied here, so nothing can reach a
  // second race even if a later bug forgets to clear it.
  const interviewGrants: Record<string, RaceGrants> = {};
  const interviewChips: Record<string, number> = {};
  const owed = carry.interview;
  carry.interview = new Map();

  const gridForRace = week.grid.map((card) => {
    const effect = owed.get(card.driverId);
    if (!effect) return card;
    if (!grantsAreEmpty(effect.grants)) interviewGrants[card.driverId] = effect.grants;
    if (effect.chips > 0) interviewChips[card.driverId] = effect.chips;
    return effect.card ?? card;
  });
  for (const [driverId, effect] of owed) {
    if (!gridIds.has(driverId)) continue;
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: 'post-race-interview',
        momentTag: 'interview.applied',
        ruleCitation: INTERVIEW_DURATION_CITE,
        actorIds: [driverId],
        positionDeltas: [],
        dice: [],
        modifiers: [{ name: 'action taken', value: effect.actionText }],
        description: `Out of last week's interview: ${effect.notes.join(' ')}`,
      },
      null,
      raceEvents,
    );
  }

  const setup = toRaceSetup({ ...week, grid: gridForRace }, {
    track: scheduled.trackId,
    totalTurns: scheduled.turns,
    raceId,
    raceName: scheduled.raceName,
    seed: raceSeed,
  });
  setup.grants = interviewGrants;
  // RWEG/p.3's own qualifying chips, plus anything the interview handed over.
  // `RaceSetup.startingChips` closes the gap this file used to only report.
  setup.startingChips = mergeChips(week.chips, interviewChips);
  // A race that throws is a rules bug, and "the TOP group has 6 slots, 8
  // vacated" thirty races into a season is unfindable without knowing which
  // race and which seed produced it.
  let race: RaceResult;
  try {
    race = runRace({ ...setup, fuming }, raceOptions);
  } catch (cause) {
    // D-018 — a `suspend` dice feed throws `DiceNeeded` to ASK for a face. That
    // is the pause, not a failure; wrapping it in a season-context Error would
    // turn a question into a crash and break hand-rolling a season race.
    if (cause instanceof DiceNeeded) throw cause;
    throw new Error(
      `race ${raceNumber} of ${seasonId} (${scheduled.raceName} at ${scheduled.trackName}, ` +
        `race seed ${raceSeed}, week seed ${weekSeed}, ${week.grid.length} starters, ` +
        `${week.feuds.length} feuds, ${fuming.length} fuming) failed: ${(cause as Error).message}`,
      { cause },
    );
  }

  // THE GAP THIS FILE USED TO ONLY REPORT IS CLOSED. `RaceSetup.startingChips`
  // exists now, so a chip earned in qualifying reaches the green flag instead
  // of being logged as lost. The BOTTOM-group ban is still enforced twice —
  // race week strips them (RWEG/p.4) and `renumber` strips them again.
  const startingChips = Object.entries(setup.startingChips ?? {}).filter(([, n]) => n > 0);
  if (startingChips.length > 0) {
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: SEASON_EVENT_TYPE,
        momentTag: 'season.starting-chips',
        ruleCitation: 'RWEG/p.3 STEP TWO — "PLACEMENT A, plus Performance Chip"',
        actorIds: startingChips.map(([id]) => id),
        positionDeltas: [],
        modifiers: startingChips.map(([id, n]) => ({
          name: 'starting chip',
          value: n,
          note: nameOf(id),
        })),
        dice: [],
        description:
          `${startingChips.length} driver${startingChips.length === 1 ? '' : 's'} take the green ` +
          'holding a performance chip.',
      },
      null,
      raceEvents,
    );
  }

  for (const event of race.events) {
    emit(raceNumber, raceId, 'race', event, event.turn, raceEvents);
  }

  // ---- what the race is worth -----------------------------------------
  const startPositions: Record<string, number> = {};
  week.grid.forEach((card, i) => {
    startPositions[card.driverId] = i + 1;
  });

  const maxTurnsLed = race.finishingOrder.reduce((best, f) => Math.max(best, f.turnsLed), 0);
  // AMBIGUITY: D-023's second turn point goes to whoever led "the most" turns
  // and says nothing about a tie. Awarded to everyone tied at the top —
  // awarding it to nobody would silently drop a point that was earned, and
  // there is no printed tiebreak to prefer one of them with.
  const mostTurnsLedIds =
    maxTurnsLed > 0
      ? race.finishingOrder.filter((f) => f.turnsLed === maxTurnsLed).map((f) => f.driverId)
      : [];
  const mostTurnsLed = new Set(mostTurnsLedIds);
  const forfeitIds = new Set(week.pointsPenalties.map((p) => p.driverId));

  // Entry events: one per driver who turned up, whatever became of him.
  for (const card of week.grid) {
    emit(
      raceNumber,
      raceId,
      'season',
      entryEvent({
        driverId: card.driverId,
        name: card.name,
        teamId: card.teamId ?? null,
        status: 'start',
        startPosition: startPositions[card.driverId],
      }),
      null,
      raceEvents,
    );
  }
  for (const driverId of week.dnq) {
    emit(
      raceNumber,
      raceId,
      'season',
      entryEvent({
        driverId,
        name: nameOf(driverId),
        teamId: roster.get(driverId)?.teamId ?? null,
        status: 'dnq',
        startPosition: null,
      }),
      null,
      raceEvents,
    );
  }
  for (const driverId of week.bubbleUnused) {
    emit(
      raceNumber,
      raceId,
      'season',
      entryEvent({
        driverId,
        name: nameOf(driverId),
        teamId: roster.get(driverId)?.teamId ?? null,
        status: 'missed-the-show',
        startPosition: null,
      }),
      null,
      raceEvents,
    );
  }

  // Result events: one per starter. Facts only — `foldSeason` scores them.
  const houseRules = seasonOptions.houseRules ?? {};
  const wonBy = lastTurnWinMethod(race);
  for (const finish of race.finishingOrder) {
    emit(
      raceNumber,
      raceId,
      'season',
      resultEvent({
        driverId: finish.driverId,
        name: finish.name,
        teamId: roster.get(finish.driverId)?.teamId ?? null,
        finishPosition: finish.place,
        turnsLed: finish.turnsLed,
        ledMostTurns: mostTurnsLed.has(finish.driverId),
        runningAtFinish: finish.status === 'running',
        outReason: finish.outReason ?? null,
        doublePoints: scheduled.doublePoints,
        pointsForfeited: forfeitIds.has(finish.driverId),
        ...(houseRules.dnfScoresZero ? { dnfScoresZero: true } : {}),
        ...(finish.place === 1 && wonBy ? { wonBy } : {}),
      }),
      null,
      raceEvents,
    );
  }

  // D-EZ-98 — stage points, when the league keeps them. Read off the running
  // order the race itself recorded at the end of each of the first two thirds;
  // nothing is rolled and nothing is guessed.
  const stageMode = houseRules.stagePoints ?? 'off';
  if (stageMode !== 'off') {
    for (const stage of stageBreaks(race)) {
      emit(
        raceNumber,
        raceId,
        'season',
        stageEvent({ ...stage, counts: stageMode === 'counted' }),
        stage.endTurn,
        raceEvents,
      );
    }
  }

  const standings = foldSeason(events);
  standingsAfterRace.push(standings);

  // ---- CONTINUITY ------------------------------------------------------
  applySanctions(week, carry, raceNumber);
  const deaths = week.unableToRace.filter((u) => u.reason === 'dead');
  for (const death of deaths) {
    carry.dead.add(death.driverId);
    fatalities.push({
      driverId: death.driverId,
      name: nameOf(death.driverId),
      raceNumber,
      text: death.text,
    });
  }

  carry.feuds = carryFeuds({
    carry,
    race,
    betweenDice,
    raceNumber,
    raceId,
    liveIds: new Set(roster.keys()),
    maxCarriedFeuds,
    nameOf,
    emit: (event) => emit(raceNumber, raceId, 'season', event, null, raceEvents),
    activeThreads: w.activeFeudThreads,
    allThreads: w.feudThreads,
  });

  // ---- THE POST RACE INTERVIEW ------------------------------------------
  //
  // One roll, at the end of the race, exactly as the sheet prints it. The
  // category is chosen by the dice and NOT by the field: a roll landing in an
  // empty band is ignored rather than re-rolled, which is why this chart
  // fires on roughly half of races and not on all of them.
  //
  // Everything it does lasts ONE race — D-EZ-15, settled off the procedure
  // line and the clarifications block together. The roster's card is never
  // written to; the effect rides in `carry` and is applied to a copy at the
  // next green flag.
  const interview = seasonOptions.postRaceInterview === false
    ? null
    : rollPostRaceInterview(interviewDice, interviewEligibility(race, carry, roster));

  if (interview) {
    let effect: InterviewEffect | null = null;
    if (!interview.ignored && interview.driverId) {
      const card = roster.get(interview.driverId);
      if (card) {
        effect = applyInterviewAction(card, interview.row.action, interviewDice);
        carry.interview.set(interview.driverId, effect);
        applyInterviewListOps(effect, carry, race, roster);
      }
    }
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: 'post-race-interview',
        momentTag: interview.ignored
          ? 'interview.ignored'
          : `interview.${interview.band.toLowerCase().replace(/_/g, '-')}`,
        ruleCitation: `${interview.ruleCitation}; ${INTERVIEW_DURATION_CITE}`,
        actorIds: interview.driverId ? [interview.driverId] : [],
        positionDeltas: [],
        dice: [
          {
            kind: '11-66',
            dice: [interview.dice.red, interview.dice.white],
            code: interview.dice.code,
            label: 'POST RACE INTERVIEW',
            faces: { red: interview.dice.red, white: interview.dice.white },
          },
        ],
        modifiers: [
          { name: 'category', value: interview.band.replace(/_/g, ' ') },
          ...(interview.driverId ? [{ name: 'driver', value: nameOf(interview.driverId) }] : []),
          ...(interview.ignored ? [] : [{ name: 'action taken', value: interview.row.action.text }]),
          // The honest exposure, on every single result rather than in a
          // footnote: 15 of the 36 rows have a reconstructed within-band
          // placement, and a player is entitled to know when he is on one.
          ...(interview.reconstructed
            ? [{ name: 'provenance', value: 'reconstructed row', detail: interview.ruleCitation }]
            : []),
          ...(effect?.noOp ? [{ name: 'no effect', value: effect.notes[0] ?? 'nothing to change' }] : []),
          ...(interview.needsHumanRuling
            ? [{ name: 'RACE OFFICIALS', value: 'a human decides this one before next race' }]
            : []),
        ],
        description:
          interview.description + (effect && !effect.noOp ? ` ${effect.notes.join(' ')}` : ''),
      },
      null,
      raceEvents,
    );
  }

  // ---- WHO GOT HURT ------------------------------------------------------
  //
  // EZRA's injury sheet only makes a driver miss races on a SERIOUS injury,
  // which is 1/36 of failed WRECK escapes and cannot arise from a Crash at
  // all. Minor and Moderate both print "The driver will race next week", so
  // neither reaches this book.
  for (const hurt of race.turns.flatMap((t) => t.injuries)) {
    if (hurt.severity !== 'serious' || hurt.racesMissed === null) continue;
    if (hurt.racesMissed === 'season') carry.injured.set(hurt.driverId, 'season');
    else if (hurt.racesMissed > 0) carry.injured.set(hurt.driverId, hurt.racesMissed);
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: 'injury',
        momentTag: 'season.injury',
        ruleCitation: hurt.ruleCitation,
        actorIds: [hurt.driverId],
        positionDeltas: [],
        dice: [],
        modifiers: hurt.modifiers,
        description: `${nameOf(hurt.driverId)} — ${hurt.description}`,
      },
      null,
      raceEvents,
    );
  }

  // ---- SUPER RARE CONSEQUENCES (D-EZ-88) ----------------------------------
  //
  // A minimal hook, kept apart on purpose: the race decides who, what and how
  // long (dice already rolled, see `careerConsequences.ts`); this only books it
  // on the same clocks the EZRA injuries above and the Race Week suspensions
  // use. Absences are read BEFORE `applySanctions` ticks them, so a count of N
  // set here is N races missed, exactly like `hurt.racesMissed`.
  for (const c of race.careerConsequences ?? []) {
    if (!roster.has(c.driverId)) continue;
    if (c.kind === 'fatality') {
      // TV EVENT Z 13's switch governs deaths in this league; with it off the
      // in-race DNF stands and nothing is carried.
      if (seasonOptions.allowDriverDeath === false || carry.dead.has(c.driverId)) continue;
      carry.dead.add(c.driverId);
      carry.injured.delete(c.driverId);
      carry.suspended.delete(c.driverId);
      fatalities.push({ driverId: c.driverId, name: nameOf(c.driverId), raceNumber, text: c.cause });
      // Into the same list Race Week's deaths use, so the rookie signing and
      // the roster removal below handle both identically.
      deaths.push({ driverId: c.driverId, reason: 'dead', text: c.cause, chart: null, code: null, ruleCitation: c.ruleCitation });
    } else if (c.kind === 'injury') {
      const was = carry.injured.get(c.driverId);
      if (c.races === 'season' || was === 'season') carry.injured.set(c.driverId, 'season');
      else carry.injured.set(c.driverId, Math.max(was ?? 0, c.races));
    } else {
      const was = carry.suspended.get(c.driverId) ?? 0;
      carry.suspended.set(c.driverId, c.races === 'season' ? SEASON_RACES : Math.max(was, c.races));
    }
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: c.kind === 'suspension' ? 'suspension' : c.kind === 'fatality' ? 'fatality' : 'injury',
        momentTag: `season.super-rare-${c.kind}`,
        ruleCitation: c.ruleCitation,
        actorIds: [c.driverId],
        positionDeltas: [],
        dice: c.die !== undefined ? [{ kind: 'd6', dice: [c.die], label: '[dice] races (D-RW-1)' }] : [],
        modifiers: [
          { name: 'races', value: c.races === 'season' ? 'rest of season' : c.races },
          { name: 'race turn', value: c.turn },
        ],
        description: `${nameOf(c.driverId)} — ${c.cause} ${describeConsequence(c, nameOf(c.driverId))}`,
      },
      null,
      raceEvents,
    );
  }

  // The TV list carries: the winner, the drivers the cameras went to
  // afterwards, and anyone still feuding (RWEG/p.3).
  //
  // "Drivers whose post-race comments made the sports shows" has no printed
  // selection rule. Read as the podium plus whoever led the most turns — the
  // four microphones a broadcast actually reaches for — PLUS whoever the
  // post-race interview chart put in front of one, which is now implemented
  // and is the closest thing the material has to a printed answer.
  const commenters = [
    ...race.finishingOrder.slice(0, 3).map((f) => f.driverId),
    ...mostTurnsLedIds,
  ];
  carry.carryOverTvIds = [
    ...new Set([
      ...(race.winnerId ? [race.winnerId] : []),
      ...commenters,
      ...carry.interviewTv,
      ...carry.feuds.flatMap((f) => [f.a, f.b]),
    ]),
  ].filter((id) => roster.has(id) && !carry.dead.has(id));
  carry.interviewTv = [];

  // Rookies, after the funeral.
  if (signRookies) {
    for (const death of deaths) {
      const signing = signRookie({
        seasonId,
        seasonSeed: state.seed,
        raceNumber,
        deadId: death.driverId,
        roster,
        teams,
        usedNames,
        index: rookies.length,
      });
      if (!signing) continue;
      rookies.push(signing);
      emit(
        raceNumber,
        raceId,
        'season',
        {
          eventType: SEASON_EVENT_TYPE,
          momentTag: 'season.rookie-signed',
          ruleCitation: `${CITE_DEATH} — "A team can always hire a rookie or suspend their season"`,
          actorIds: [signing.driverId, signing.replacing],
          positionDeltas: [],
          modifiers: [
            { name: 'driver-name', value: signing.name },
            { name: 'team', value: signing.teamId },
            { name: 'replacing', value: nameOf(signing.replacing) },
          ],
          dice: [],
          description: `${teams.get(signing.teamId)?.name ?? signing.teamId} sign rookie ${signing.name} for the rest of the season.`,
        },
        null,
        raceEvents,
      );
    }
  }
  // An independent's death, or a chartered team that chose not to replace him,
  // simply removes the entry.
  for (const death of deaths) roster.delete(death.driverId);

  settleTransfers({
    carry,
    roster,
    teams,
    raceNumber,
    raceId,
    betweenDice,
    nameOf,
    emit: (event) => emit(raceNumber, raceId, 'season', event, null, raceEvents),
  });

  const result: SeasonRaceResult = {
    raceNumber,
    scheduled,
    week,
    race,
    startPositions,
    mostTurnsLedIds,
    pointsForfeitedIds: [...forfeitIds],
    absences,
    standings,
    events: raceEvents,
    weekSeed,
    raceSeed,
  };
  races.push(result);

  // ---- THE PLAYOFF -----------------------------------------------------
  if (raceNumber === REGULAR_SEASON_RACES) {
    w.playoffSeeds = seedPlayoff(standings, (id) => isTitleEligible(id, carry, roster));
    w.contenders = w.playoffSeeds.map((s) => s.driverId);
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: SEASON_EVENT_TYPE,
        momentTag: 'season.playoff.seeded',
        ruleCitation: CITE_PLAYOFF_QUALIFY,
        actorIds: w.contenders,
        positionDeltas: [],
        modifiers: w.playoffSeeds.map((s) => ({
          name: `seed-${s.seed}`,
          value: s.name,
          note: s.viaWin ? `${s.wins} win${s.wins === 1 ? '' : 's'}` : `${s.points} points`,
        })),
        dice: [],
        description: `The playoff field is set: ${w.playoffSeeds.map((s) => `${s.seed}. ${s.name}`).join(', ')}.`,
      },
      null,
      raceEvents,
    );
  }

  const spec = PLAYOFF_ROUNDS.find((r) => r.lastRace === raceNumber);
  if (spec && w.contenders) {
    const roundRaces = [];
    for (let n = spec.firstRace; n <= spec.lastRace; n++) roundRaces.push(n);
    const roundResult = closeRound({
      spec,
      contenders: w.contenders,
      roundRaces,
      standings,
      races,
    });
    w.playoffRounds.push(roundResult);
    emit(
      raceNumber,
      raceId,
      'season',
      {
        eventType: SEASON_EVENT_TYPE,
        momentTag: spec.advancing === null ? 'season.playoff.champion' : 'season.playoff.cut',
        ruleCitation: spec.advancing === null ? CITE_SEASON : CITE_ROUND_WIN,
        actorIds: [...roundResult.advanced, ...roundResult.eliminated],
        positionDeltas: [],
        modifiers: [
          ...roundResult.advanced.map((id) => ({
            name: 'advances',
            value: nameOf(id),
            note: `${roundResult.roundPoints[id] ?? 0} points in the ${roundResult.label}`,
          })),
          ...roundResult.eliminated.map((id) => ({
            name: 'eliminated',
            value: nameOf(id),
            note: `${roundResult.roundPoints[id] ?? 0} points in the ${roundResult.label}`,
          })),
        ],
        dice: [],
        description:
          spec.advancing === null
            ? `${nameOf(roundResult.advanced[0] ?? '')} is the champion.`
            : `${roundResult.label}: ${roundResult.eliminated.map(nameOf).join(', ')} eliminated.`,
      },
      null,
      raceEvents,
    );
    w.contenders = roundResult.advanced;
  }

  // ---- THE BOOKS, IF ANYONE IS KEEPING THEM ------------------------------
  //
  // Settled here, at the end of the weekend, off the SAME function the
  // whole-season fold calls. `totalRaces` is the season's LENGTH and not the
  // number of races run so far: Sagliano prorates yearly sponsorship, salary
  // and automaker support over the season, and a week-7 book that thought the
  // year was seven races long would pay a seventh of the year's money out
  // every Sunday.
  let finance: RaceFinanceResult | null = null;
  if (w.finance) {
    finance = runSeasonFinanceRace({
      seasonId,
      race: result,
      profiles: w.finance.profiles,
      state: w.finance.contracts,
      previous: w.finance.previous ?? undefined,
      totalRaces: state.schedule.races.length,
      options: w.finance.options,
    });
    w.finance = {
      ...w.finance,
      contracts: finance.state,
      previous: raceFactsForNextWeek(race),
      events: [...w.finance.events, ...finance.events],
      races: [...w.finance.races, finance],
      sponsorChanges: [
        ...w.finance.sponsorChanges,
        ...finance.sponsorChanges.map((change) => ({ ...change, raceNumber })),
      ],
      gloomyEconomy: finance.gloomyEconomy,
    };
  }

  return { race, week, result, state: dehydrate(state, w), finance };
}

// ---------------------------------------------------------------------------
// The end of the year
// ---------------------------------------------------------------------------

/**
 * Fold the season into the report `runSeason` has always returned.
 *
 * Refuses an unfinished season. A `SeasonResult` from race 20 would claim a
 * null champion and close every live feud with `season-ended` — two invented
 * results dressed as facts. A caller who wants the standings mid-year reads
 * `state.standingsAfterRace`, which is exactly as true after race 20 as it is
 * after race 36.
 */
export function finishSeason(state: SeasonState): SeasonResult {
  if (!isSeasonComplete(state)) {
    throw new RangeError(
      `${state.seasonId} has ${racesRemaining(state)} race${racesRemaining(state) === 1 ? '' : 's'} left; ` +
        'a season report is not a thing that exists yet',
    );
  }

  const pointsStandings =
    state.standingsAfterRace[state.standingsAfterRace.length - 1] ?? foldSeason(state.events);
  const championshipRound = state.playoff.rounds.find((r) => r.round === 'championship');
  const finalFour = state.playoff.rounds.find((r) => r.round === 'round-of-8')?.advanced ?? [];
  const championId = championshipRound?.advanced[0] ?? null;
  // D-EZ-90 — the champion is first in the table everything downstream reads.
  const finalStandings = championshipOrder(pointsStandings, { championId, rounds: state.playoff.rounds });

  // Close every feud that was still live when the season ran out of races.
  // The threads are copied rather than written to, so calling `finishSeason`
  // twice gives the same answer twice and the state stays a record of the
  // season as it was played.
  const stillLive = new Set(state.activeFeudThreads.map(([, index]) => index));
  const feudThreads = state.feudThreads.map((thread, index) =>
    stillLive.has(index)
      ? { ...thread, races: [...thread.races], endedRace: SEASON_RACES, endedBy: 'season-ended' as const }
      : { ...thread, races: [...thread.races] },
  );

  return {
    seasonId: state.seasonId,
    seed: state.seed,
    schedule: state.schedule,
    races: [...state.races],
    standingsAfterRace: [...state.standingsAfterRace],
    finalStandings,
    playoff: {
      seeds: state.playoff.seeds,
      rounds: state.playoff.rounds,
      finalFour,
      championId,
      championRacePoints:
        championId === null ? null : (championshipRound?.roundPoints[championId] ?? null),
    },
    championId,
    events: [...state.events],
    finalRoster: state.roster.map((c) => ({ ...c })),
    rookies: [...state.rookies],
    fatalities: [...state.fatalities],
    feudThreads: feudThreads.sort((a, b) => a.startedRace - b.startedRace || (a.a < b.a ? -1 : 1)),
    pointsStandings,
    inherited: state.inherited ?? null,
    carryOut: {
      feuds: state.carry.feuds.map((f) => ({ a: f.a, b: f.b })),
      suspended: state.carry.suspended.filter(([, n]) => n > 0).map(([id, n]) => [id, n] as [string, number]),
      injured: state.carry.injured
        .filter((pair): pair is [string, number] => typeof pair[1] === 'number' && pair[1] > 0)
        .map(([id, n]) => [id, n] as [string, number]),
    },
  };
}

/**
 * D-EZ-98 — the running order at the end of stages one and two.
 *
 * Stages are THIRDS OF THE RACE'S SCHEDULED TURNS (Howard's sheet), ending on
 * turn ⌊N/3⌋ and ⌊2N/3⌋. The third stage ends at the checkered flag and is the
 * race result itself, which the finishing ladder already scores. The order is
 * the race's own recorded state after that turn — only cars still running.
 */
export function stageBreaks(race: RaceResult): { stage: number; endTurn: number; order: { driverId: string; name: string }[] }[] {
  const n = race.scheduledTurns;
  if (!Number.isFinite(n) || n < 3) return [];
  const ends = [Math.floor(n / 3), Math.floor((2 * n) / 3)];
  const out: { stage: number; endTurn: number; order: { driverId: string; name: string }[] }[] = [];
  ends.forEach((endTurn, i) => {
    const turn = race.turns.find((t) => t.turn === endTurn) ?? race.turns.filter((t) => t.turn <= endTurn).pop();
    if (!turn) return;
    out.push({
      stage: i + 1,
      endTurn,
      order: turn.state.grid.slice(0, 10).map((car) => ({ driverId: car.card.driverId, name: car.card.name })),
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// Hydration — the state as data, the state as machinery
// ---------------------------------------------------------------------------

/**
 * The working form: exactly the Maps and Sets the old loop kept in locals.
 *
 * There are two forms of the same facts on purpose. The SERIALISED form is what
 * a save file and a screen see; the WORKING form is what the rule code sees,
 * unchanged from the day it was written, which is why moving the loop into a
 * state machine could not change a single result.
 */
interface Working {
  roster: Map<string, DriverCard>;
  teams: Map<string, SeasonTeam>;
  usedNames: Set<string>;
  carry: Carry;
  events: SeasonEvent[];
  races: SeasonRaceResult[];
  standingsAfterRace: SeasonStandings[];
  rookies: RookieSigning[];
  fatalities: SeasonCasualty[];
  feudThreads: FeudThread[];
  activeFeudThreads: Map<string, FeudThread>;
  sequence: number;
  playoffSeeds: PlayoffSeed[];
  playoffRounds: PlayoffRoundResult[];
  contenders: string[] | null;
  finance: SeasonFinanceTrackState | null;
}

/**
 * Copy everything the race is going to write to.
 *
 * The copies are what make `runSeasonRace` pure. Note what is NOT copied: the
 * driver CARDS, the ledger EVENTS, the finished RACE records. Nothing in the
 * engine ever writes to one of those in place — a transfer replaces a card, an
 * interview builds a new one — so sharing them costs nothing and keeps a
 * thirty-six-race season from re-copying its whole history every Sunday.
 */
function hydrate(state: SeasonState): Working {
  const feudThreads = state.feudThreads.map((t) => ({ ...t, races: [...t.races] }));
  const activeFeudThreads = new Map<string, FeudThread>();
  for (const [key, index] of state.activeFeudThreads) {
    const thread = feudThreads[index];
    // A dangling index means a hand-edited or half-written state. Refuse it
    // rather than silently starting a fresh thread for a feud that is already
    // running — that would double-count it in the season's story.
    if (!thread) throw new Error(`season state is inconsistent: active feud ${key} points at thread #${index}`);
    activeFeudThreads.set(key, thread);
  }

  return {
    roster: new Map(state.roster.map((c) => [c.driverId, c])),
    teams: new Map(state.teams.map((t) => [t.teamId, { ...t, driverIds: [...t.driverIds] }])),
    usedNames: new Set(state.usedNames),
    carry: {
      feuds: state.carry.feuds.map((f) => ({ ...f })),
      fuming: [...state.carry.fuming],
      suspended: new Map(state.carry.suspended),
      injured: new Map(state.carry.injured),
      dead: new Set(state.carry.dead),
      inTransfer: new Map(state.carry.inTransfer.map(([id, t]) => [id, { ...t }])),
      carryOverTvIds: [...state.carry.carryOverTvIds],
      interview: new Map(state.carry.interview),
      interviewTv: [...state.carry.interviewTv],
      interviewFirstDuel: [...state.carry.interviewFirstDuel],
    },
    events: [...state.events],
    races: [...state.races],
    standingsAfterRace: [...state.standingsAfterRace],
    rookies: [...state.rookies],
    fatalities: [...state.fatalities],
    feudThreads,
    activeFeudThreads,
    sequence: state.sequence,
    playoffSeeds: [...state.playoff.seeds],
    playoffRounds: [...state.playoff.rounds],
    contenders: state.playoff.contenders ? [...state.playoff.contenders] : null,
    finance: state.finance,
  };
}

/** The working form, back to data, one race further on. */
function dehydrate(state: SeasonState, w: Working): SeasonState {
  // The active-feud map and the thread log hold the SAME objects while a race
  // is running; on the way out the map becomes indexes into the log, because a
  // shared reference does not survive a save file. See `SeasonState`.
  const indexOf = new Map<FeudThread, number>(w.feudThreads.map((t, i) => [t, i]));
  const activeFeudThreads: [string, number][] = [];
  for (const [key, thread] of w.activeFeudThreads) {
    const index = indexOf.get(thread);
    if (index === undefined) throw new Error(`feud ${key} is live but is not in the thread log`);
    activeFeudThreads.push([key, index]);
  }

  return {
    ...state,
    nextRace: state.nextRace + 1,
    roster: [...w.roster.values()],
    teams: [...w.teams.values()],
    usedNames: [...w.usedNames],
    carry: {
      feuds: w.carry.feuds,
      fuming: w.carry.fuming,
      suspended: [...w.carry.suspended],
      injured: [...w.carry.injured],
      dead: [...w.carry.dead],
      inTransfer: [...w.carry.inTransfer],
      carryOverTvIds: w.carry.carryOverTvIds,
      interview: [...w.carry.interview],
      interviewTv: w.carry.interviewTv,
      interviewFirstDuel: w.carry.interviewFirstDuel,
    },
    events: w.events,
    races: w.races,
    standingsAfterRace: w.standingsAfterRace,
    rookies: w.rookies,
    fatalities: w.fatalities,
    feudThreads: w.feudThreads,
    activeFeudThreads,
    sequence: w.sequence,
    playoff: { seeds: w.playoffSeeds, rounds: w.playoffRounds, contenders: w.contenders },
    finance: w.finance,
  };
}

// ---------------------------------------------------------------------------
// D-018 — hand-rolling a SEASON race
// ---------------------------------------------------------------------------

/** The engine has stopped inside a season race and wants a face. */
export interface ManualSeasonNeeded {
  status: 'need';
  request: DiceRequest;
  /** Every turn that COMPLETED before the engine stopped. */
  turns: readonly TurnResult[];
  /** Every face given so far, in order, with its provenance. */
  stream: readonly DieDraw[];
  /** One line a human can act on: "Roll the RED die (1–6)." */
  prompt: string;
}

/** The season race ran to the checkered flag on the faces it was given. */
export interface ManualSeasonDone {
  status: 'done';
  step: SeasonRaceStep;
}

export type ManualSeasonStep = ManualSeasonNeeded | ManualSeasonDone;

/**
 * ONE HAND-ROLLED SEASON RACE, played die by die — the thing `manualRace.ts`
 * could only do for an exhibition.
 *
 *     const session = new ManualSeasonRace(state);
 *     let step = session.start();
 *     while (step.status === 'need') step = session.supply(faceFromTheTable());
 *     const next = step.step.state;      // the season, one race on
 *
 * SAME MECHANISM, SAME REASON. `runSeasonRace(state, {dice: {faces: F}})` is a
 * pure function of F — the race week is rebuilt from `weekSeed`, the race from
 * `raceSeed`, the carry from the state, and none of them reads a clock or a
 * global. So the engine can be run to the first die F does not contain, asked
 * for it, and run again from the top: it retraces the identical week and the
 * identical race, consumes the identical faces, and goes one die further. See
 * the header of `manualRace.ts` for why replay beat a generator rewrite.
 *
 * WHAT IT COSTS HERE THAT IT DOES NOT COST THERE: a replay re-runs the RACE
 * WEEK as well as the race — the TV list, qualifying, the whole of RWEG/p.3.
 * Measured at roughly 2-6 ms on top of a 15-50 ms race, so a nine-hundred-face
 * race costs a few extra seconds of arithmetic spread over an evening at a
 * table. Nobody will see it, and the alternative is caching a half-run season
 * race, which is a mutable snapshot of exactly the kind this file exists to
 * abolish.
 */
export class ManualSeasonRace {
  private readonly state: SeasonState;
  private readonly options: SeasonRaceOptions;
  private readonly given: number[] = [];
  private last: ManualSeasonStep | null = null;
  private completedTurns: TurnResult[] = [];

  constructor(state: SeasonState, options: SeasonRaceOptions = {}) {
    if (options.dice || options.race?.dice || options.weekend) {
      // Two sources of faces is one too many, and quietly preferring either one
      // would make the saved stream disagree with what the player typed.
      throw new Error(
        'ManualSeasonRace owns the dice feed — pass the starting faces to `start(faces)`, not in options',
      );
    }
    this.state = state;
    this.options = options;
  }

  /** Every face entered so far, in table order. This IS the save file. */
  get faces(): readonly number[] {
    return this.given;
  }

  /** Turns that finished before the current pause. Empty before `start`. */
  get turns(): readonly TurnResult[] {
    return this.completedTurns;
  }

  /** Begin — or begin again from faces already rolled. */
  start(faces: readonly number[] = []): ManualSeasonStep {
    this.given.length = 0;
    this.given.push(...faces);
    return this.run('suspend');
  }

  /**
   * Hand the engine one or more faces and let it carry on.
   *
   * The first face is checked against the die actually being asked for before
   * it is accepted, so a 7 typed for a d6 is refused at the door rather than
   * silently shifting every later roll onto the wrong question. The ones after
   * it answer questions the engine has not asked yet, so they are checked by
   * `Dice.d` as they are drawn — which throws for the same reason.
   */
  supply(...faces: number[]): ManualSeasonStep {
    const pending = this.last;
    if (!pending || pending.status !== 'need') {
      throw new Error('nothing is waiting on a die — call start() first, or the race is already finished');
    }
    if (faces.length === 0) throw new RangeError('supply() needs at least one face');
    const first = faces[0];
    if (!Number.isInteger(first) || first < 1 || first > pending.request.sides) {
      throw new RangeError(
        `${first} is not a face of the d${pending.request.sides} the engine is asking for` +
          (pending.request.role ? ` (${pending.request.role})` : ''),
      );
    }
    this.given.push(...faces);
    return this.run('suspend');
  }

  /** Take back the last `n` faces. Free and exact — the race is a function of them. */
  undo(n = 1): ManualSeasonStep {
    for (let i = 0; i < n && this.given.length > 0; i++) this.given.pop();
    return this.run('suspend');
  }

  /**
   * "That's enough — run the rest."
   *
   * Finishes on the seeded PRNG from wherever the hand-rolled faces stop, and
   * returns the completed step so the season can move on. The crossover is
   * recorded face by face in `race.diceStream`.
   */
  finishWithEngine(): SeasonRaceStep {
    const step = this.run('prng');
    if (step.status !== 'done') {
      // Cannot happen: the `prng` policy never suspends. Stated rather than
      // cast away, because a silent lie here would be a lie about the dice.
      throw new Error('the engine was asked to finish the race and stopped for a die anyway');
    }
    return step.step;
  }

  /** The replay. The only place `runSeasonRace` is called, always from the top. */
  private run(exhausted: 'suspend' | 'prng'): ManualSeasonStep {
    const turns: TurnResult[] = [];
    const callerOnTurn = this.options.race?.onTurn;
    try {
      const step = runSeasonRace(this.state, {
        ...this.options,
        // THE WHOLE WEEKEND off one list — the race week's own draws first
        // (qualifying, the TV list, the feuds), then the race. `runSeasonRace`
        // cuts the list where the week actually stopped; see the note there for
        // why the boundary cannot be worked out in advance.
        weekend: { faces: [...this.given], exhausted },
        race: {
          ...this.options.race,
          // The pause unwinds `runRace`'s stack and its own `turns` array goes
          // with it, so completed turns can only get out through here. A
          // caller's own `onTurn` still fires — this wraps it, never replaces it.
          onTurn: (t) => {
            turns.push(t);
            callerOnTurn?.(t);
          },
        },
      });
      this.completedTurns = turns;
      this.last = { status: 'done', step };
      return this.last;
    } catch (err) {
      if (!(err instanceof DiceNeeded)) throw err;
      // Not an error — the pause. Everything the screen needs is on it.
      this.completedTurns = turns;
      this.last = {
        status: 'need',
        request: err.request,
        turns,
        stream: err.stream,
        prompt: describeRequest(err.request),
      };
      return this.last;
    }
  }
}

/**
 * Play a whole season race from faces already rolled, with no interaction.
 *
 * The non-interactive twin of `ManualSeasonRace`, and the thing the harness
 * measures: a short face list comes back `need`, and a race that finishes on
 * hand faces alone comes back `done`. Both are countable.
 */
export function runSeasonRaceOnFaces(
  state: SeasonState,
  faces: readonly number[],
  options: SeasonRaceOptions = {},
): ManualSeasonStep {
  return new ManualSeasonRace(state, options).start(faces);
}

// ---------------------------------------------------------------------------
// Sanctions
// ---------------------------------------------------------------------------

/**
 * Tick the clocks and start the new ones.
 *
 * ORDER MATTERS. Everybody already serving sits out THIS race, so their counter
 * comes down first; a sanction issued during THIS race week already cost the
 * driver this race (`Suspension.includesThisRace`, and an injury row reads
 * "cannot race this week"), so it is booked afterwards with one race already
 * served.
 */
function applySanctions(week: RaceWeekResult, carry: Carry, _raceNumber: number): void {
  for (const [driverId, left] of [...carry.suspended]) {
    const next = left - 1;
    if (next > 0) carry.suspended.set(driverId, next);
    else carry.suspended.delete(driverId);
  }
  for (const [driverId, left] of [...carry.injured]) {
    if (left === 'season') continue;
    const next = left - 1;
    if (next > 0) carry.injured.set(driverId, next);
    else carry.injured.delete(driverId);
  }
  for (const [driverId, transfer] of [...carry.inTransfer]) {
    carry.inTransfer.set(driverId, { ...transfer, racesLeft: transfer.racesLeft - 1 });
  }

  for (const suspension of week.suspensions) {
    // D-028 §1 — 1d6 races, this one first. `races - 1` is what is left after it.
    const remaining = Math.max(0, suspension.races - 1);
    if (remaining > 0) {
      carry.suspended.set(suspension.driverId, Math.max(carry.suspended.get(suspension.driverId) ?? 0, remaining));
    }
  }
  for (const injury of week.injuries) {
    if (injury.duration === 'season') carry.injured.set(injury.driverId, 'season');
    // 'this-race' has already been served: raceWeek kept him out of this grid.
  }
  for (const transfer of week.teamTransfers) {
    carry.inTransfer.set(transfer.driverId, {
      racesLeft: Math.max(1, transfer.weeks),
      fromTeamId: transfer.fromTeamId ?? null,
    });
  }
}

// ---------------------------------------------------------------------------
// Feuds between races (RB/xvi)
// ---------------------------------------------------------------------------

interface CarryFeudArgs {
  carry: Carry;
  race: RaceResult;
  betweenDice: Dice;
  raceNumber: number;
  raceId: string;
  liveIds: Set<string>;
  maxCarriedFeuds: number;
  nameOf: (id: string) => string;
  emit: (event: RuleEvent) => void;
  /** The feud currently running between a pair, if any. */
  activeThreads: Map<string, FeudThread>;
  /** Every feud the season has run, closed or not. */
  allThreads: FeudThread[];
}

/** The bits of a `DuelResult` this needs, read structurally off the ledger. */
interface DuelShape {
  instigatorId?: unknown;
  targetId?: unknown;
  declined?: unknown;
  won?: unknown;
}

/**
 * Roll RB/xvi twice over.
 *
 * FIRST, for every driver who lost a duel in this race — which is the printed
 * case: "when a driver 'spins out' after losing a DUEL, he goes on the FUME
 * list. Or … you might decide that a FEUD has just been started." EZRA turns
 * that into a d6 and this rolls it, once per duel, for the loser.
 *
 * SECOND, for every feud already on the list. AMBIGUITY: this second use is an
 * extension — RB/xvi is printed for duel losers and no source says how a feud
 * ends. Without something, feuds accumulate for 36 weeks and every FEUD/FUME
 * roll in the back half of the season finds a full list. The same d6 is used
 * because it is the only distribution the game gives for this question, and it
 * retires half the list a week on average, which is what a grudge does.
 */
function carryFeuds(args: CarryFeudArgs): FeudEntry[] {
  const { carry, race, betweenDice, raceNumber, liveIds, nameOf, emit, activeThreads, allThreads } = args;
  const carried: FeudEntry[] = [];
  const fuming = new Set<string>();

  // A pair who fall out, make up, and fall out again in October are two
  // stories, not one — so a closed thread is never reopened.
  const note = (feud: FeudEntry, wasLiveThisRace: boolean): FeudThread => {
    const key = feudKey(feud);
    let thread = activeThreads.get(key);
    if (!thread) {
      thread = { a: feud.a, b: feud.b, races: [], startedRace: raceNumber, endedRace: null, endedBy: null };
      activeThreads.set(key, thread);
      allThreads.push(thread);
    }
    if (wasLiveThisRace && !thread.races.includes(raceNumber)) thread.races.push(raceNumber);
    return thread;
  };
  const close = (feud: FeudEntry, endedBy: FeudThread['endedBy']): void => {
    const key = feudKey(feud);
    const thread = activeThreads.get(key);
    if (!thread) return;
    thread.endedRace = raceNumber;
    thread.endedBy = endedBy;
    activeThreads.delete(key);
  };

  // Every feud that was live during the race — the list race week handed over
  // plus anything the race itself started (a RARE result, a feud/fume row).
  for (const feud of race.state.feuds) note(feud, true);

  // ---- 1. duel losers ----------------------------------------------------
  //
  // "when a driver 'SPINS OUT' after losing a DUEL" (RB/xvi). The condition is
  // the spin, not the loss: losing a duel hands the loser a TROUBLE result
  // (p.ix) and he escapes it more often than not. A driver who kept the car
  // straight has nothing to be furious about, so only a loser who was actually
  // COLLECTED rolls on the table. Taking every loss instead roughly quadruples
  // the feud list and makes a grudge meaningless.
  const duelPairs: { loser: string; other: string }[] = [];
  race.events.forEach((event, i) => {
    if (event.eventType !== 'duel') return;
    const duel = event as unknown as DuelShape;
    if (duel.declined === true) return;
    if (typeof duel.instigatorId !== 'string' || typeof duel.targetId !== 'string') return;
    const loser = duel.won === true ? duel.targetId : duel.instigatorId;
    const other = duel.won === true ? duel.instigatorId : duel.targetId;
    if (!liveIds.has(loser) || !liveIds.has(other) || loser === other) return;
    // The trouble a duel causes is resolved immediately, so it is the next
    // trouble event of the same turn.
    let spun = false;
    for (let j = i + 1; j < race.events.length; j++) {
      const next = race.events[j];
      if (next.turn !== event.turn) break;
      if (next.eventType !== 'trouble') continue;
      const collected = (next as unknown as { collectedIds?: unknown }).collectedIds;
      spun = Array.isArray(collected) && collected.includes(loser);
      break;
    }
    if (spun) duelPairs.push({ loser, other });
  });

  const newFeuds: FeudEntry[] = [];
  for (const pair of duelPairs) {
    const d6 = betweenDice.d6();
    const outcome = feudLoserOutcome(d6);
    if (outcome === 'feud' || outcome === 'both') {
      const feud = { a: pair.loser, b: pair.other };
      if (!newFeuds.some((f) => sameFeud(f, feud)) && !race.state.feuds.some((f) => sameFeud(f, feud))) {
        newFeuds.push(feud);
      }
    }
    if (outcome === 'fume' || outcome === 'both') fuming.add(pair.loser);
    emit({
      eventType: SEASON_EVENT_TYPE,
      momentTag: `season.feud-loser.${outcome}`,
      ruleCitation: CITE_FEUD_LOSERS,
      actorIds: [pair.loser, pair.other],
      positionDeltas: [],
      modifiers: [{ name: 'RB/xvi outcome', value: outcome, note: `${nameOf(pair.loser)} lost the duel` }],
      dice: [{ kind: 'd6', dice: [d6], label: 'RB/xvi feud losers' }],
      description:
        outcome === 'none'
          ? `${nameOf(pair.loser)} shrugs off losing to ${nameOf(pair.other)}.`
          : outcome === 'fume'
            ? `${nameOf(pair.loser)} is fuming after losing to ${nameOf(pair.other)}.`
            : outcome === 'feud'
              ? `${nameOf(pair.loser)} and ${nameOf(pair.other)} have a feud now.`
              : `${nameOf(pair.loser)} is fuming, and he and ${nameOf(pair.other)} have a feud now.`,
    });
  }

  // ---- 2. does an existing feud survive the week? ------------------------
  for (const feud of race.state.feuds) {
    if (!liveIds.has(feud.a) || !liveIds.has(feud.b)) continue;
    const d6 = betweenDice.d6();
    const outcome = feudLoserOutcome(d6);
    const thread = note(feud, false);
    if (outcome === 'feud' || outcome === 'both') {
      carried.push({ ...feud });
    } else {
      close(feud, outcome === 'fume' ? 'became-fume' : 'cooled-off');
    }
    if (outcome === 'fume' || outcome === 'both') {
      fuming.add(feud.a);
      fuming.add(feud.b);
    }
    emit({
      eventType: SEASON_EVENT_TYPE,
      momentTag: `season.feud-carry.${outcome}`,
      ruleCitation: CITE_FEUD_LOSERS,
      actorIds: [feud.a, feud.b],
      positionDeltas: [],
      modifiers: [
        { name: 'RB/xvi outcome', value: outcome },
        { name: 'races-live', value: (thread.races.length || 1) },
      ],
      dice: [{ kind: 'd6', dice: [d6], label: 'RB/xvi feud carry-over' }],
      description:
        outcome === 'none'
          ? `${nameOf(feud.a)} and ${nameOf(feud.b)} let it go.`
          : outcome === 'fume'
            ? `${nameOf(feud.a)} and ${nameOf(feud.b)} are done feuding, and both are still furious.`
            : outcome === 'feud'
              ? `${nameOf(feud.a)} and ${nameOf(feud.b)} take it to next week.`
              : `${nameOf(feud.a)} and ${nameOf(feud.b)} take it to next week, both of them fuming.`,
    });
  }

  for (const feud of newFeuds) {
    if (carried.some((f) => sameFeud(f, feud))) continue;
    carried.push(feud);
    note(feud, false);
  }

  carry.fuming = [...fuming].filter((id) => liveIds.has(id));

  // The safety rail. Should never bind — the RB/xvi roll retires half the list
  // a week — but if it does, the feuds it drops are closed properly rather than
  // left hanging in the thread log.
  const kept = carried.slice(0, args.maxCarriedFeuds);
  for (const dropped of carried.slice(args.maxCarriedFeuds)) close(dropped, 'cooled-off');
  return kept;
}

// ---------------------------------------------------------------------------
// Transfers and rookies
// ---------------------------------------------------------------------------

interface SettleArgs {
  carry: Carry;
  roster: Map<string, DriverCard>;
  teams: Map<string, SeasonTeam>;
  raceNumber: number;
  raceId: string;
  betweenDice: Dice;
  nameOf: (id: string) => string;
  emit: (event: RuleEvent) => void;
}

/**
 * D-028 §4 — Z 16 has a driver quit and sign elsewhere. We have no contract
 * system (D-013), so the move is a straight transfer to a team with a seat
 * open, chosen at random from the ones that have one.
 *
 * AMBIGUITY: "chosen by the commissioner or at random among teams with a
 * vacancy" — with no commissioner in the loop, random it is. If no team has a
 * seat, he runs independent, which is the only other thing a licensed driver
 * without a ride can do.
 */
function settleTransfers(args: SettleArgs): void {
  const { carry, roster, teams, betweenDice, nameOf, emit } = args;
  for (const [driverId, transfer] of [...carry.inTransfer]) {
    if (transfer.racesLeft > 0) continue;
    carry.inTransfer.delete(driverId);
    const card = roster.get(driverId);
    if (!card) continue;

    const openings = [...teams.values()].filter(
      (t) => t.teamId !== transfer.fromTeamId && t.driverIds.length < TEAM_SIZE,
    );
    const to = openings.length > 0 ? openings[betweenDice.d(openings.length) - 1] : null;

    if (transfer.fromTeamId) {
      const from = teams.get(transfer.fromTeamId);
      if (from) from.driverIds = from.driverIds.filter((id) => id !== driverId);
    }
    if (to) to.driverIds = [...to.driverIds, driverId];
    roster.set(driverId, { ...card, teamId: to?.teamId });

    emit({
      eventType: SEASON_EVENT_TYPE,
      momentTag: to ? 'season.transfer.signed' : 'season.transfer.independent',
      ruleCitation: CITE_TRANSFER,
      actorIds: [driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'driver-name', value: nameOf(driverId) },
        { name: 'team', value: to?.teamId ?? 'independent' },
        { name: 'from-team', value: transfer.fromTeamId ?? 'independent' },
      ],
      dice: [],
      description: to
        ? `${nameOf(driverId)} signs with ${to.name}.`
        : `${nameOf(driverId)} finds no seat and runs independent.`,
    });
  }
}

interface RookieArgs {
  seasonId: string;
  seasonSeed: number;
  raceNumber: number;
  deadId: string;
  roster: Map<string, DriverCard>;
  teams: Map<string, SeasonTeam>;
  usedNames: Set<string>;
  index: number;
}

/**
 * D-028 §2 — "A team can always hire a rookie or suspend their season."
 *
 * RULING: a CHARTERED team signs a rookie; an INDEPENDENT does not, because
 * there is no shop behind the car. That is the whole of the decision, and it
 * needs no invented die roll: the charter is the thing that obliges a team to
 * field an entry (D-024, and the charter/independent split `field.ts` already
 * builds).
 */
function signRookie(args: RookieArgs): RookieSigning | null {
  const dead = args.roster.get(args.deadId);
  const teamId = dead?.teamId;
  if (!teamId) return null;
  const team = args.teams.get(teamId);
  if (!team) return null;

  const dice = new Dice(raceSeedFor(args.seasonSeed, args.raceNumber, SALT_ROOKIE + args.index));
  const name = nextFreeName(dice, args.usedNames);
  args.usedNames.add(name);
  // QUALIFIED BY SEASON. `rk-RR-NN` alone collided the second time a driver
  // died in the same race number in a later season — a "duplicate driver" throw,
  // or two careers merged in the record book.
  const driverId = `rk-${args.seasonId}-r${String(args.raceNumber).padStart(2, '0')}-${String(args.index + 1).padStart(2, '0')}`;
  // The same die as ever picks the number; if a licensed driver already runs
  // it, walk on to the next free one (no extra die, so nothing else shifts).
  const taken = new Set([...args.roster.values()].map((c) => c.carNumber));
  const rolled = dice.d(CAR_NUMBERS.length) - 1;
  let carNumber = CAR_NUMBERS[rolled];
  for (let i = 0; i < CAR_NUMBERS.length && taken.has(carNumber); i++) {
    carNumber = CAR_NUMBERS[(rolled + i + 1) % CAR_NUMBERS.length];
  }
  const created = createDriver(dice, {
    driverId,
    name,
    carNumber,
    teamId,
  });
  // D-EZ-93 — a rookie's age, hashed (no die spent).
  const rookie = { ...created, card: { ...created.card, age: rookieAge(`${driverId}|${name}`) } };

  args.roster.set(driverId, rookie.card);
  team.driverIds = [...team.driverIds.filter((id) => id !== args.deadId), driverId];

  return { driverId, name, teamId, replacing: args.deadId, raceNumber: args.raceNumber };
}

function nextFreeName(dice: Dice, used: Set<string>): string {
  for (let attempt = 0; attempt < 200; attempt++) {
    const candidate = `${FIRST_NAMES[dice.d(FIRST_NAMES.length) - 1]} ${SURNAMES[dice.d(SURNAMES.length) - 1]}`;
    if (!used.has(candidate)) return candidate;
  }
  for (const surname of SURNAMES) {
    for (const first of FIRST_NAMES) {
      const candidate = `${first} ${surname}`;
      if (!used.has(candidate)) return candidate;
    }
  }
  throw new Error('name pools exhausted signing a rookie');
}

// ---------------------------------------------------------------------------
// The playoff
// ---------------------------------------------------------------------------

/** A driver can still win the title if he can still turn up. */
function isTitleEligible(driverId: string, carry: Carry, roster: Map<string, DriverCard>): boolean {
  if (!roster.has(driverId)) return false;
  if (carry.dead.has(driverId)) return false;
  if (carry.injured.get(driverId) === 'season') return false;
  return true;
}

/**
 * D-012 — "every race winner qualifies first; remaining berths go to points;
 * ties broken on average points per start."
 *
 * A winner is IN before any points are read, which is the whole point of the
 * rule: it is what makes a one-win backmarker's afternoon matter in November.
 * Only if more drivers win races than there are berths does the win list itself
 * have to be cut, and then it is cut on wins, then points, then D-012's
 * average-points-per-start tiebreak.
 */
export function seedPlayoff(
  standings: SeasonStandings,
  eligible: (driverId: string) => boolean,
  berths = 16,
): PlayoffSeed[] {
  const field = standings.drivers.filter((d) => d.starts > 0 && eligible(d.driverId));
  const winners = field.filter((d) => d.wins > 0).sort(byWinsThenPoints);
  const rest = field.filter((d) => d.wins === 0).sort(compareDrivers);

  const chosen: { stat: DriverSeasonStats; viaWin: boolean }[] = [
    ...winners.slice(0, berths).map((stat) => ({ stat, viaWin: true })),
  ];
  for (const stat of rest) {
    if (chosen.length >= berths) break;
    chosen.push({ stat, viaWin: false });
  }
  // A season with fewer than `berths` drivers who ever started is possible only
  // in a test, but the bracket must still be well formed.
  return chosen.map((entry, i) => ({
    driverId: entry.stat.driverId,
    name: entry.stat.name,
    seed: i + 1,
    viaWin: entry.viaWin,
    wins: entry.stat.wins,
    points: entry.stat.points,
    avgPointsPerStart: entry.stat.avgPointsPerStart,
  }));
}

function byWinsThenPoints(a: DriverSeasonStats, b: DriverSeasonStats): number {
  if (b.wins !== a.wins) return b.wins - a.wins;
  return compareDrivers(a, b);
}

interface CloseRoundArgs {
  spec: { round: PlayoffRound; label: string; firstRace: number; lastRace: number; advancing: number | null };
  contenders: readonly string[];
  roundRaces: number[];
  standings: SeasonStandings;
  races: readonly SeasonRaceResult[];
}

/**
 * Cut a round.
 *
 * The round's points are the SAME fold over a slice of the SAME ledger — there
 * is no second scoring path anywhere in this project, which is what stops the
 * playoff and the championship standings from ever disagreeing.
 */
function closeRound(args: CloseRoundArgs): PlayoffRoundResult {
  const { spec, contenders, roundRaces, standings, races } = args;
  const roundPoints: Record<string, number> = {};
  for (const driverId of contenders) {
    const stat = standings.byDriver[driverId];
    let total = 0;
    for (const raceNumber of roundRaces) total += stat?.pointsByRace[raceNumber] ?? 0;
    roundPoints[driverId] = total;
  }

  // D-012 / EZRA — a round win advances a driver on the spot.
  const autoAdvanced = contenders.filter((driverId) =>
    roundRaces.some((n) => races[n - 1]?.race.winnerId === driverId),
  );

  if (spec.advancing === null) {
    // The championship race. Highest points among the final four takes it;
    // the printed maximum makes a race winner unbeatable, so the tiebreaks
    // below only ever fire on a genuine dead heat.
    //
    // AMBIGUITY: D-012 stops at "Championship — 4" and prints no tiebreak.
    // Ranked on the round's points, then the better finish in the race itself,
    // then the season's points — nothing invented, just the facts already on
    // the sheet, in the order a scorer would reach for them.
    const finale = races[spec.lastRace - 1];
    const finishOf = (driverId: string): number =>
      finale?.race.finishingOrder.find((f) => f.driverId === driverId)?.place ?? Number.MAX_SAFE_INTEGER;
    const ranked = [...contenders].sort(
      (a, b) =>
        (roundPoints[b] ?? 0) - (roundPoints[a] ?? 0) ||
        finishOf(a) - finishOf(b) ||
        (standings.byDriver[b]?.points ?? 0) - (standings.byDriver[a]?.points ?? 0) ||
        (a < b ? -1 : 1),
    );
    return {
      round: spec.round,
      label: spec.label,
      races: roundRaces,
      contenders: [...contenders],
      roundPoints,
      autoAdvanced,
      advanced: ranked.slice(0, 1),
      eliminated: ranked.slice(1),
    };
  }

  const auto = new Set(autoAdvanced);
  const onPoints = contenders
    .filter((id) => !auto.has(id))
    .sort(
      (a, b) =>
        (roundPoints[b] ?? 0) - (roundPoints[a] ?? 0) ||
        (standings.byDriver[b]?.points ?? 0) - (standings.byDriver[a]?.points ?? 0) ||
        (standings.byDriver[b]?.avgPointsPerStart ?? 0) - (standings.byDriver[a]?.avgPointsPerStart ?? 0) ||
        (a < b ? -1 : 1),
    );

  const room = Math.max(0, spec.advancing - autoAdvanced.length);
  const advanced = [...autoAdvanced, ...onPoints.slice(0, room)];
  const eliminated = onPoints.slice(room);

  return {
    round: spec.round,
    label: spec.label,
    races: roundRaces,
    contenders: [...contenders],
    roundPoints,
    autoAdvanced,
    advanced,
    eliminated,
  };
}
